import { useState, useEffect, useMemo } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';
import { InventoryItem, InventoryGroup } from '../types';
import clsx from 'clsx';

// ============================================================================
// PRICING MATRICES - Used for auto-calculating MSRP from dimensions
// ============================================================================
type ProductType = 'cvw' | 'solar94' | 'solar99' | 'insect';

const PRICING_MATRICES: Record<ProductType, {
  heights: number[];
  widths: number[];
  prices: Record<number, number[]>;
}> = {
  cvw: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3988.60, 4111.96, 4239.13, 4370.24, 4505.40, 4644.74, 4788.40, 4936.49, 5089.17, 5246.56, 5408.83, 5576.11, 5748.57, 5926.36, 6109.65, 6298.61, 6493.41, 6694.24, 6901.27, 7114.72, 7334.76],
      96: [4107.40, 4234.43, 4365.39, 4500.41, 4639.59, 4783.09, 4931.05, 5083.62, 5240.94, 5403.15, 5570.39, 5742.81, 5920.56, 6103.77, 6291.62, 6486.21, 6686.81, 6893.62, 7106.83, 7326.63, 7553.22],
      108: [4230.60, 4361.44, 4496.33, 4635.40, 4778.76, 4926.55, 5078.92, 5235.99, 5397.99, 5565.06, 5737.35, 5914.99, 6098.14, 6286.93, 6480.34, 6680.76, 6887.38, 7100.39, 7319.99, 7546.39, 7779.78],
      120: [4358.20, 4492.99, 4631.95, 4775.20, 4922.89, 5075.15, 5232.11, 5393.93, 5560.75, 5732.73, 5910.03, 6092.82, 6281.25, 6475.52, 6675.79, 6882.26, 7095.11, 7314.55, 7540.77, 7773.99, 8014.43],
      132: [4489.10, 4627.94, 4771.07, 4918.63, 5070.75, 5227.58, 5389.26, 5555.93, 5727.77, 5904.92, 6087.54, 6275.82, 6469.91, 6670.01, 6876.30, 7088.97, 7308.22, 7534.25, 7767.26, 8007.49, 8255.14],
      144: [4623.30, 4766.29, 4913.70, 5065.67, 5222.34, 5383.86, 5550.37, 5722.03, 5898.99, 6081.44, 6269.53, 6463.43, 6663.33, 6869.41, 7081.87, 7300.89, 7526.70, 7759.48, 7999.46, 8246.87, 8501.93],
      156: [4761.90, 4909.18, 5061.01, 5217.53, 5378.90, 5545.26, 5716.76, 5893.57, 6075.84, 6263.75, 6457.48, 6657.19, 6863.09, 7075.35, 7294.17, 7519.76, 7752.33, 7992.10, 8239.28, 8494.10, 8756.80],
      168: [4904.90, 5056.60, 5212.99, 5374.21, 5540.43, 5711.78, 5888.43, 6070.55, 6258.30, 6451.85, 6651.40, 6857.11, 7069.18, 7287.82, 7513.22, 7745.58, 7985.14, 8232.10, 8486.70, 8749.18, 9019.77],
      180: [5052.30, 5208.56, 5369.65, 5535.72, 5706.93, 5883.43, 6065.39, 6252.98, 6446.37, 6645.74, 6851.28, 7063.18, 7281.63, 7506.83, 7739.00, 7978.35, 8225.10, 8479.49, 8741.74, 9012.10, 9290.83],
      192: [5204.10, 5365.05, 5530.98, 5702.04, 5878.39, 6060.20, 6247.63, 6440.85, 6640.06, 6845.42, 7057.13, 7275.39, 7500.41, 7732.38, 7971.52, 8218.07, 8472.23, 8734.26, 9004.39, 9282.88, 9569.98]
    }
  },
  solar94: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3275, 3376, 3480, 3588, 3699, 3813, 3931, 4053, 4178, 4308, 4441, 4578, 4720, 4866, 5016, 5171, 5331, 5496, 5666, 5841, 6022],
      96: [3378, 3483, 3590, 3701, 3816, 3934, 4055, 4181, 4310, 4444, 4581, 4723, 4869, 5019, 5174, 5335, 5500, 5670, 5845, 6026, 6212],
      108: [3445.40, 3552, 3662, 3775, 3892, 4012, 4136, 4264, 4396, 4532, 4672, 4817, 4966, 5119, 5278, 5441, 5609, 5783, 5961, 6146, 6336],
      120: [3548.60, 3658, 3771, 3888, 4008, 4132, 4260, 4392, 4528, 4668, 4812, 4961, 5114, 5273, 5436, 5604, 5777, 5956, 6140, 6330, 6526],
      132: [3615.70, 3728, 3843, 3962, 4084, 4211, 4341, 4475, 4613, 4756, 4903, 5055, 5211, 5372, 5538, 5710, 5886, 6068, 6256, 6450, 6649],
      144: [3770.80, 3887, 4008, 4132, 4259, 4391, 4527, 4667, 4811, 4960, 5113, 5272, 5435, 5603, 5776, 5955, 6139, 6329, 6524, 6726, 6934],
      156: [4138.20, 4266, 4398, 4534, 4674, 4819, 4968, 5122, 5280, 5443, 5612, 5785, 5964, 6149, 6339, 6535, 6737, 6945, 7160, 7382, 7610],
      168: [4244.90, 4376, 4512, 4651, 4795, 4943, 5096, 5254, 5416, 5584, 5756, 5934, 6118, 6307, 6502, 6703, 6911, 7124, 7345, 7572, 7806],
      180: [4315.30, 4449, 4586, 4728, 4874, 5025, 5181, 5341, 5506, 5676, 5852, 6033, 6219, 6412, 6610, 6815, 7025, 7243, 7467, 7697, 7936],
      192: [4423.10, 4560, 4701, 4846, 4996, 5151, 5310, 5474, 5644, 5818, 5998, 6184, 6375, 6572, 6775, 6985, 7201, 7423, 7653, 7890, 8134]
    }
  },
  solar99: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3438.75, 3544.80, 3654.00, 3767.40, 3883.95, 4003.65, 4127.55, 4255.65, 4386.90, 4523.40, 4663.05, 4806.90, 4956.00, 5109.30, 5266.80, 5429.55, 5597.55, 5770.80, 5949.30, 6133.05, 6323.10],
      96: [3546.90, 3657.15, 3769.50, 3886.05, 4006.80, 4130.70, 4257.75, 4390.05, 4525.50, 4666.20, 4810.05, 4959.15, 5112.45, 5269.95, 5432.70, 5601.75, 5775.00, 5953.50, 6137.25, 6327.30, 6522.60],
      108: [3617.67, 3729.60, 3845.10, 3963.75, 4086.60, 4212.60, 4342.80, 4477.20, 4615.80, 4758.60, 4905.60, 5057.85, 5214.30, 5374.95, 5541.90, 5713.05, 5889.45, 6072.15, 6259.05, 6453.30, 6652.80],
      120: [3725.73, 3840.90, 3959.55, 4082.40, 4208.40, 4338.60, 4473.00, 4611.60, 4754.40, 4901.40, 5052.60, 5209.05, 5369.70, 5536.65, 5707.80, 5884.20, 6065.85, 6253.80, 6447.00, 6646.50, 6852.30],
      132: [3796.49, 3914.40, 4035.15, 4160.10, 4288.20, 4421.55, 4558.05, 4698.75, 4843.65, 4993.80, 5148.15, 5307.75, 5471.55, 5640.60, 5814.90, 5995.50, 6180.30, 6371.40, 6568.80, 6772.50, 6981.45],
      144: [3959.34, 4081.35, 4208.40, 4338.60, 4471.95, 4610.55, 4753.35, 4900.35, 5051.55, 5208.00, 5368.65, 5535.60, 5706.75, 5883.15, 6064.80, 6252.75, 6445.95, 6645.45, 6850.20, 7062.30, 7280.70],
      156: [4345.11, 4479.30, 4617.90, 4760.70, 4907.70, 5059.95, 5216.40, 5378.10, 5544.00, 5715.15, 5892.60, 6074.25, 6262.20, 6456.45, 6655.95, 6861.75, 7073.85, 7292.25, 7518.00, 7751.10, 7990.50],
      168: [4457.15, 4594.80, 4737.60, 4883.55, 5034.75, 5190.15, 5350.80, 5516.70, 5686.80, 5863.20, 6043.80, 6230.70, 6423.90, 6622.35, 6827.10, 7038.15, 7256.55, 7480.20, 7712.25, 7950.60, 8196.30],
      180: [4531.07, 4671.45, 4815.30, 4964.40, 5117.70, 5276.25, 5440.05, 5608.05, 5781.30, 5959.80, 6144.60, 6334.65, 6529.95, 6732.60, 6940.50, 7155.75, 7376.25, 7605.15, 7840.35, 8081.85, 8332.80],
      192: [4644.26, 4788.00, 4936.05, 5088.30, 5245.80, 5408.55, 5575.50, 5747.70, 5926.20, 6108.90, 6297.90, 6493.20, 6693.75, 6900.60, 7113.75, 7334.25, 7561.05, 7794.15, 8035.65, 8284.50, 8540.70]
    }
  },
  insect: {
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3158, 3256, 3356, 3460, 3567, 3678, 3791, 3909, 4030, 4154, 4283, 4415, 4552, 4692, 4838, 4987, 5141, 5300, 5464, 5633, 5808],
      96: [3256, 3357, 3461, 3568, 3678, 3792, 3909, 4030, 4154, 4283, 4415, 4552, 4693, 4838, 4987, 5142, 5301, 5465, 5634, 5808, 5988],
      108: [3318, 3420, 3526, 3635, 3747, 3863, 3983, 4106, 4233, 4364, 4499, 4638, 4781, 4929, 5082, 5239, 5401, 5568, 5740, 5918, 6101],
      120: [3414, 3520, 3629, 3741, 3857, 3976, 4099, 4226, 4357, 4491, 4630, 4773, 4921, 5073, 5230, 5392, 5559, 5731, 5908, 6090, 6279],
      132: [3475, 3582, 3693, 3807, 3925, 4047, 4172, 4301, 4434, 4571, 4712, 4858, 5008, 5163, 5323, 5487, 5657, 5832, 6012, 6198, 6390],
      144: [3625, 3737, 3852, 3971, 4094, 4221, 4351, 4486, 4625, 4768, 4915, 5067, 5224, 5385, 5552, 5724, 5901, 6083, 6271, 6465, 6665],
      156: [3999, 4122, 4250, 4381, 4517, 4656, 4800, 4949, 5102, 5260, 5422, 5590, 5763, 5941, 6125, 6314, 6510, 6711, 6919, 7133, 7353],
      168: [4090, 4216, 4347, 4481, 4620, 4763, 4910, 5062, 5218, 5380, 5546, 5718, 5894, 6077, 6265, 6458, 6658, 6864, 7076, 7295, 7521],
      180: [4155, 4283, 4416, 4552, 4693, 4838, 4988, 5142, 5301, 5465, 5634, 5808, 5988, 6173, 6364, 6561, 6764, 6973, 7189, 7411, 7640],
      192: [4256, 4388, 4523, 4663, 4807, 4956, 5109, 5267, 5430, 5598, 5771, 5950, 6134, 6324, 6519, 6721, 6929, 7143, 7364, 7592, 7826]
    }
  }
};

// Build fabric-to-product-type map from catalog for pricing lookup
function buildFabricToProductType(catalog: FabricCatalogType[]): Record<string, ProductType> {
  const map: Record<string, ProductType> = {};
  for (const ft of catalog) {
    if (['solar94', 'solar99', 'cvw', 'insect'].includes(ft.pricingCategory)) {
      map[ft.code] = ft.pricingCategory as ProductType;
    }
  }
  return map;
}

// Get MSRP price from pricing matrix
function getScreenMsrp(productType: ProductType, width: number, height: number): number | null {
  const matrix = PRICING_MATRICES[productType];
  if (!matrix) return null;

  const minWidth = matrix.widths[0];
  const maxWidth = matrix.widths[matrix.widths.length - 1];
  const minHeight = matrix.heights[0];
  const maxHeight = matrix.heights[matrix.heights.length - 1];

  // Check bounds
  if (width > maxWidth || height > maxHeight) return null;

  // Find height bucket (round up to next bucket)
  let heightBucket: number;
  if (height <= minHeight) {
    heightBucket = minHeight;
  } else {
    heightBucket = matrix.heights.find(h => h >= height) || maxHeight;
  }

  // Find width bucket (round up to next bucket)
  let widthBucket: number;
  if (width <= minWidth) {
    widthBucket = minWidth;
  } else {
    widthBucket = matrix.widths.find(w => w >= width) || maxWidth;
  }

  const widthIndex = matrix.widths.indexOf(widthBucket);
  const prices = matrix.prices[heightBucket];

  if (!prices || widthIndex < 0 || widthIndex >= prices.length) return null;

  return prices[widthIndex];
}

// BOM calculation variables
interface BomVariables {
  W_system: number;
  H_system: number;
  // Box width for housing/roller formulas. Derived separately from W_system —
  // the box keeps the box deduction but NOT the u-channel deduction.
  boxW: number;
  // Raw u-channel deduction (0 / 0.375 / 0.75). W_system already has it baked
  // in; only the roller tube formula subtracts it again, same as the backend.
  u_channel_ded: number;
  holes: number;
  seam_count: number;
  // Configuration variables
  u_channel: string;
  u_channel_qty: number;
  screw_cover: string;
  screw_cover_qty: number;
  brush_size: number;
  motor_type: string;
  fabric_type: string;
  frame_color: string;
  window_panel: boolean;
  window_count: number;
  remote_qty: number;
  remote_type: string;
}

// Shipping method options
type ShippingMethod = 'will_call' | 'freight';

// Product line version interface for BOM
interface ProductLineVersion {
  id: string;
  productLineId: string;
  versionNumber: string;
  versionName: string | null;
  status: 'active' | 'retired' | 'warranty_only';
}

interface ProductLineWithVersions {
  id: string;
  name: string;
  code: string | null;
  isActive: boolean;
  versions?: ProductLineVersion[];
}

// Options for BOM calculation
interface BomOptions {
  // Product Line & Version
  productLineId: string;
  productLineVersionId: string;
  // Configuration Options
  systemType: 'v_track' | 'open_roll';
  uChannel: 'none' | 'left' | 'right' | 'both';
  screwCover: 'none' | 'left' | 'right' | 'both';
  brushSize: '1' | '2';
  shippingMethod: ShippingMethod;
  shippingCost: number;
  windowPanel: boolean;
  windowCount: number;
  // Frame/Material
  frameColor: string;
  fabricType: string;
  fabricColor: string;
  // Controls
  // Factory deductions — same semantics as the order form. U-channel defaults
  // OFF (backend: apply_u_channel_deduction ?? false); box defaults ON
  // (backend: apply_box_deduction ?? true).
  applyUChannelDeduction: boolean;
  applyBoxDeduction: boolean;
  motorType: 'none' | 'keder' | 'alpha' | 'somfy';
  remoteType: string;
  remoteQty: number;
  wallSwitchType: string;
  wallSwitchQty: number;
}

// Fabric catalog types (loaded from API)
interface FabricCatalogColor {
  code: string;
  name: string;
  partNumber: string | null;
  sortOrder: number;
}
interface FabricCatalogType {
  code: string;
  name: string;
  pricingCategory: string;
  group: string;
  sortOrder: number;
  colors: FabricCatalogColor[];
}

// Build part map from catalog for BOM filtering
function buildFabricPartMap(catalog: FabricCatalogType[]): Record<string, Record<string, string>> {
  const map: Record<string, Record<string, string>> = {};
  for (const ft of catalog) {
    map[ft.code] = {};
    for (const c of ft.colors) {
      if (c.partNumber) map[ft.code][c.code] = c.partNumber;
    }
  }
  return map;
}

// Build UI dropdown data from catalog
function buildFabricTypesFromCatalog(catalog: FabricCatalogType[]): { value: string; label: string; group: string }[] {
  return catalog.map(ft => ({ value: ft.code, label: ft.name, group: ft.group }));
}

function buildFabricColorsFromCatalog(catalog: FabricCatalogType[]): Record<string, { value: string; label: string }[]> {
  const colors: Record<string, { value: string; label: string }[]> = {};
  for (const ft of catalog) {
    colors[ft.code] = ft.colors.map(c => ({ value: c.code, label: c.name }));
  }
  return colors;
}

// Remote type options based on motor type
const REMOTE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  none: [],
  keder: [
    { value: 'none', label: 'None' },
    { value: 'keder-1ch', label: 'Keder 1-ch' },
    { value: 'keder-16ch', label: 'Keder 16-ch' },
  ],
  alpha: [
    { value: 'none', label: 'None' },
    { value: 'alpha-pioneer-1ch', label: 'Alpha Pioneer 1-ch' },
    { value: 'alpha-pioneer-4ch', label: 'Alpha Pioneer 4-ch' },
    { value: 'alpha-pioneer-8ch-lcd', label: 'Alpha Pioneer 8-ch LCD' },
    { value: 'alpha-pioneer-16ch-lcd', label: 'Alpha Pioneer 16-ch LCD' },
  ],
  somfy: [
    { value: 'none', label: 'None' },
    { value: 'situo-1ch', label: 'Situo 1-ch' },
    { value: 'situo-5ch', label: 'Situo 5-ch' },
    { value: 'telis-1ch', label: 'Telis 1-ch' },
    { value: 'telis-4ch', label: 'Telis 4-ch' },
    { value: 'telis-16ch', label: 'Telis 16-ch' },
  ],
};

// Wall switch options based on motor type
const WALL_SWITCH_OPTIONS: Record<string, { value: string; label: string }[]> = {
  none: [],
  keder: [
    { value: 'none', label: 'None' },
    { value: 'keder-wall-switch', label: 'Keder Wall Switch' },
  ],
  alpha: [
    { value: 'none', label: 'None' },
    { value: 'alpha-rts-wall-switch', label: 'Alpha RTS Wall Switch' },
  ],
  somfy: [
    { value: 'none', label: 'None' },
    { value: 'somfy-rts-wall-switch', label: 'Somfy RTS Wall Switch' },
  ],
};

// Calculate screw cover quantity based on selection
function calculateScrewCoverQty(screwCover: string): number {
  switch (screwCover) {
    case 'none':
      return 0;
    case 'left':
    case 'right':
      return 1;
    case 'both':
      return 2;
    default:
      return 0;
  }
}

// Calculate U-Channel quantity based on selection
function calculateUChannelQty(uChannel: string): number {
  switch (uChannel) {
    case 'none':
      return 0;
    case 'left':
    case 'right':
      return 1;
    case 'both':
      return 2;
    default:
      return 0;
  }
}

// Calculated BOM line item
interface BomLineItem {
  id: string;
  partNumber: string;
  hasCutFormula?: boolean;
  inventoryGroup?: string;
  description: string;
  formula: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  stockTakeType?: string;
  // Cut length fields for extrusions
  cutLength?: number;
  stockLength?: number;
  proratedCost?: number;
  isExtrusion?: boolean;
}

// Custom BOM item (user-selected)
interface CustomBomItem {
  id: string;
  partNumber: string;
  description: string;
  quantity: number;
  unitCost: number;
  inventoryGroup?: string;
  cutSize?: number;         // Calculated cut size from cut_formula
  stockLength?: number;     // Stock length for proration (extrusions only)
  isExtrusion?: boolean;    // Whether this is an extrusion (affects cost calc)
  stockTakeType?: string;   // 'linear_from_roll', 'cut_length', etc.
}

// Calculate holes based on height
function calculateHoles(heightInches: number): number {
  if (heightInches <= 0) return 0;
  // 8" from top, 12" from bottom, 16-20" spacing between
  const usableHeight = heightInches - 8 - 12; // Remove top and bottom margins
  if (usableHeight <= 0) return 2; // Minimum 2 holes (top and bottom)
  const spacing = 18; // Average spacing
  const middleHoles = Math.floor(usableHeight / spacing);
  return middleHoles + 2; // Add top and bottom holes
}

// Calculate seam count based on HEIGHT (not width!)
// Formula: IF (H_system + 10) > roll_width THEN seam_count = 1 ELSE seam_count = 0
// The +10 is the actual fabric cut height (H_system + 10)
function calculateSeamCount(heightInches: number, rollWidth: number = 126): number {
  // Required fabric height = screen height + 10" (actual cut sheet dimension)
  const requiredFabricHeight = heightInches + 10;
  // If required height exceeds roll width, seam is needed
  if (requiredFabricHeight > rollWidth) {
    // For very tall screens, calculate panels needed (usually just 1 seam = 2 panels)
    return Math.floor(requiredFabricHeight / rollWidth);
  }
  return 0;
}

// Parse and evaluate a formula
// Calculate track sleeves based on height (every ~20" of track height)
function calculateSleeves(heightInches: number): number {
  if (heightInches <= 36) return 1;
  return Math.ceil((heightInches - 20) / 20) + 1;
}

function evaluateFormula(formula: string, vars: BomVariables): number | null {
  if (!formula) return null;

  // Calculate derived values
  const springs = vars.holes; // Springs = holes (spring assemblies)
  const sleeves = calculateSleeves(vars.H_system); // Track sleeves based on height

  try {
    // Replace variable names with values
    // boxW is its own dimension — it carries the box deduction but not the
    // u-channel deduction. It used to be aliased to W_system here, so this page
    // costed housing/roller/box-brush off an undeducted width and drifted from
    // the cut sheet the floor actually builds to.
    const boxW = vars.boxW;
    let expr = formula
      // Box width (must be before W_system to avoid partial match)
      .replace(/boxW/g, boxW.toString())
      // Dimension variables
      .replace(/W_system/gi, vars.W_system.toString())
      .replace(/H_system/gi, vars.H_system.toString())
      .replace(/u_channel_ded/gi, vars.u_channel_ded.toString())
      .replace(/factory_ded/gi, '0')
      .replace(/holes/gi, vars.holes.toString())
      .replace(/seam_count/gi, vars.seam_count.toString())
      // Derived variables
      .replace(/springs/gi, springs.toString())
      .replace(/sleeves/gi, sleeves.toString())
      // New variables
      .replace(/u_channel_qty/gi, vars.u_channel_qty.toString())
      .replace(/u_channel/gi, vars.u_channel ? '1' : '0')
      .replace(/screw_cover_qty/gi, vars.screw_cover_qty.toString())
      .replace(/brush_size/gi, vars.brush_size.toString())
      .replace(/biased_cut/gi, vars.biased_cut ? '1' : '0')
      .replace(/window_panel/gi, vars.window_panel ? '1' : '0')
      // Remote quantity from the Controls dropdown — 0 when no motor selected,
      // otherwise the user-entered Remote QTY. Used by remote SKU quantity_formula.
      .replace(/remote_qty/gi, Math.max(0, Number(vars.remote_qty ?? 0)).toString())
      // Operators
      .replace(/×/g, '*')
      .replace(/÷/g, '/');

    // Simple math evaluation (only allows numbers and basic operators)
    // eslint-disable-next-line no-new-func
    const result = new Function(`return ${expr}`)();
    return typeof result === 'number' && !isNaN(result) ? result : null;
  } catch {
    return null;
  }
}

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

// Assembly type for the selector
interface Assembly {
  id: string;
  name: string;
  description: string;
  itemCount: number;
  isFullScreen?: boolean;
  isCustom?: boolean;
  isFabricPanel?: boolean;
  isFabricOnly?: boolean;
}

export default function BomPage() {
  // Data state
  const [groups, setGroups] = useState<InventoryGroup[]>([]);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [powderCoatCharge, setPowderCoatCharge] = useState(495); // Default $495, loaded from settings

  // Dynamic fabric catalog (loaded from API)
  const [fabricCatalog, setFabricCatalog] = useState<FabricCatalogType[]>([]);
  const fabricPartMap = useMemo(() => buildFabricPartMap(fabricCatalog), [fabricCatalog]);
  const fabricTypes = useMemo(() => buildFabricTypesFromCatalog(fabricCatalog), [fabricCatalog]);
  const fabricColorMap = useMemo(() => buildFabricColorsFromCatalog(fabricCatalog), [fabricCatalog]);
  const fabricToProductType = useMemo(() => buildFabricToProductType(fabricCatalog), [fabricCatalog]);

  // Selection state
  const [selectedAssembly, setSelectedAssembly] = useState<Assembly | null>(null);

  // Custom BOM state
  const [customBomItems, setCustomBomItems] = useState<CustomBomItem[]>([]);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');

  // Saved BOMs management state
  interface SavedBom {
    id: string;
    name: string;
    assemblyName?: string;
    width: number;
    height: number;
    totalCost: number;
    createdAt: string;
    createdBy: string;
    items?: any[];
  }
  const [savedBoms, setSavedBoms] = useState<SavedBom[]>([]);
  const [showSavedBomsModal, setShowSavedBomsModal] = useState(false);
  const [loadingSavedBoms, setLoadingSavedBoms] = useState(false);
  const [editingBomId, setEditingBomId] = useState<string | null>(null);
  const [editingBomName, setEditingBomName] = useState('');
  const [loadedSavedBomId, setLoadedSavedBomId] = useState<string | null>(null);

  // Assembly editing state
  const [showEditAssemblyModal, setShowEditAssemblyModal] = useState(false);
  const [editingAssembly, setEditingAssembly] = useState<Assembly | null>(null);
  const [assemblyItemSearch, setAssemblyItemSearch] = useState('');
  const [savingAssemblyChanges, setSavingAssemblyChanges] = useState(false);

  // Full Screen Unit - independent BOM (stored in custom_bom_templates with special name)
  const FULL_SCREEN_BOM_NAME = '__FULL_SCREEN_UNIT_DEFAULT__';
  const [fullScreenBomItems, setFullScreenBomItems] = useState<string[]>([]); // Item IDs in Full Screen Unit
  const [fullScreenBomLoaded, setFullScreenBomLoaded] = useState(false);

  // BOM Editing state (for standard assemblies)
  const [isEditingBom, setIsEditingBom] = useState(false);
  const [editedBomItems, setEditedBomItems] = useState<Map<string, { removed: boolean; quantityOverride?: number }>>(new Map());
  const [addedBomItems, setAddedBomItems] = useState<CustomBomItem[]>([]);
  const [showBomItemPicker, setShowBomItemPicker] = useState(false);
  const [customBomName, setCustomBomName] = useState('');
  const [savingCustomBom, setSavingCustomBom] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Multi-Assembly Build state - allows combining multiple assemblies into one BOM
  interface BuildAssembly {
    id: string;
    assemblyName: string;
    width: number;
    height: number;
    frameColor: string;
    fabricType?: string;
    fabricColor?: string;
    motorType?: string;
    items: {
      id: string;
      partNumber: string;
      description: string;
      quantity: number;
      unitCost: number;
      cutLength?: number;
    }[];
  }
  const [multiBuildItems, setMultiBuildItems] = useState<BuildAssembly[]>([]);
  const [showMultiBuild, setShowMultiBuild] = useState(false);

  // Special Order state (create orders from BOM)
  const [showSpecialOrderModal, setShowSpecialOrderModal] = useState(false);
  const [specialOrderData, setSpecialOrderData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    markupPercent: 400,
    shippingAddress: { street: '', city: '', state: '', zip: '' },
    shippingCost: 0,
    internalNotes: '',
    customerNotes: '',
  });
  const [creatingSpecialOrder, setCreatingSpecialOrder] = useState(false);

  // Freight quote state for special orders
  const [freightQuotes, setFreightQuotes] = useState<any[]>([]);
  const [selectedFreightQuote, setSelectedFreightQuote] = useState<any>(null);
  const [loadingFreightQuotes, setLoadingFreightQuotes] = useState(false);
  const [freightError, setFreightError] = useState('');
  const [freightAccessorials, setFreightAccessorials] = useState({
    liftgate: false,
    residential: false,
    inside: false,
  });

  // Main BOM freight quote state
  const [bomFreightZip, setBomFreightZip] = useState('');
  const [bomFreightQuotes, setBomFreightQuotes] = useState<any[]>([]);
  const [bomFreightLoading, setBomFreightLoading] = useState(false);
  const [bomFreightError, setBomFreightError] = useState('');
  const [bomFreightAccessorials, setBomFreightAccessorials] = useState({
    liftgate: false,
    residential: false,
    inside: false,
  });
  const [bomFreightDimensions, setBomFreightDimensions] = useState({
    length: 48,
    width: 12,
    height: 12,
    weight: 50,
    pallets: 1,
  });
  const [dealers, setDealers] = useState<any[]>([]);
  const [selectedDealerId, setSelectedDealerId] = useState<string>('');
  // Load active dealers for the special-order dealer picker (bills to their
  // portal + applies their discount). Previously never loaded, so KS- special
  // orders had no dealer → no portal billing, no discount.
  useEffect(() => {
    api.get('/dealers?limit=500&status=active')
      .then((res: any) => setDealers(Array.isArray(res) ? res : (res?.data || [])))
      .catch(() => setDealers([]));
  }, []);

  // Input state
  const [width, setWidth] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [options, setOptions] = useState<BomOptions>({
    // Product Line & Version
    productLineId: '',
    productLineVersionId: '',
    // Configuration Options
    systemType: 'v_track',
    applyUChannelDeduction: false,
    applyBoxDeduction: true,
    uChannel: 'none',
    screwCover: 'none',
    brushSize: '1',
    shippingMethod: 'will_call',
    shippingCost: 0,
    windowPanel: false,
    windowCount: 1,
    // Frame/Material
    frameColor: 'black',
    fabricType: 'omega_pro_3',
    fabricColor: 'black',
    // Controls
    motorType: 'keder',
    remoteType: 'keder-1ch',
    remoteQty: 1,
    wallSwitchType: 'none',
    wallSwitchQty: 0,
  });

  // Product lines with versions for version dropdown
  const [productLines, setProductLines] = useState<ProductLineWithVersions[]>([]);

  // Derived values based on options
  const uChannelQty = useMemo(() => calculateUChannelQty(options.uChannel), [options.uChannel]);
  const screwCoverQty = useMemo(() => {
    return calculateScrewCoverQty(options.screwCover);
  }, [options.screwCover]);

  // Margin calculator state
  const [msrpOverride, setMsrpOverride] = useState<string>('');
  const [dealerDiscount, setDealerDiscount] = useState<string>('40');

  // Auto-calculate MSRP from dimensions and fabric type
  // NOTE: calculatedMsrp is declared later (after activeTotalCost) because it depends on activeTotalCost

  // Fetch freight quotes for special order
  const fetchFreightQuotes = async () => {
    if (!specialOrderData.shippingAddress.zip || specialOrderData.shippingAddress.zip.length !== 5) {
      setFreightError('Please enter a valid 5-digit ZIP code');
      return;
    }

    setLoadingFreightQuotes(true);
    setFreightError('');
    setFreightQuotes([]);
    setSelectedFreightQuote(null);

    try {
      // Estimate shipment dimensions for an assembly
      const w = parseFloat(width) || 48;
      const h = parseFloat(height) || 48;
      const estimatedWeight = Math.ceil((w * h) / 144 * 2) + 20; // rough estimate

      const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/freight/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_auth_token')}`,
        },
        body: JSON.stringify({
          destination_zip: specialOrderData.shippingAddress.zip,
          length: Math.ceil(Math.max(w, 48)),
          width: 12,
          height: 12,
          weight: estimatedWeight,
          pallets: 1,
          freight_class: 85,
          accessorials: freightAccessorials,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get freight quotes');
      }

      const data = await response.json();

      if (data.success && data.allQuotes?.length > 0) {
        setFreightQuotes(data.allQuotes);
        // Auto-select cheapest
        setSelectedFreightQuote(data.allQuotes[0]);
        setSpecialOrderData(prev => ({ ...prev, shippingCost: data.allQuotes[0].totalAmount }));
      } else if (data.fallback) {
        // Use fallback rate
        setFreightQuotes([{
          quoteId: 'fallback',
          carrierName: 'Estimated',
          serviceDescription: 'LTL Freight',
          totalAmount: data.rate,
          transitTime: data.transitTime || '3-7 days',
        }]);
        setSelectedFreightQuote({
          quoteId: 'fallback',
          carrierName: 'Estimated',
          totalAmount: data.rate,
        });
        setSpecialOrderData(prev => ({ ...prev, shippingCost: data.rate }));
      } else {
        setFreightError(data.error || 'No quotes available for this destination');
      }
    } catch (err: any) {
      console.error('Freight quote error:', err);
      setFreightError(err.message || 'Failed to get freight quotes');
    } finally {
      setLoadingFreightQuotes(false);
    }
  };

  // Fetch freight quotes for main BOM calculator
  const fetchBomFreightQuotes = async () => {
    if (!bomFreightZip || bomFreightZip.length !== 5) {
      setBomFreightError('Enter a valid 5-digit ZIP code');
      return;
    }

    setBomFreightLoading(true);
    setBomFreightError('');
    setBomFreightQuotes([]);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/freight/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_auth_token')}`,
        },
        body: JSON.stringify({
          destination_zip: bomFreightZip,
          length: bomFreightDimensions.length,
          width: bomFreightDimensions.width,
          height: bomFreightDimensions.height,
          weight: bomFreightDimensions.weight,
          pallets: bomFreightDimensions.pallets,
          freight_class: 85,
          accessorials: bomFreightAccessorials,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get freight quotes');
      }

      const data = await response.json();

      if (data.success && data.allQuotes?.length > 0) {
        setBomFreightQuotes(data.allQuotes);
      } else if (data.fallback) {
        setBomFreightQuotes([{
          quoteId: 'fallback',
          carrierName: 'Estimated',
          serviceDescription: 'LTL Freight',
          totalAmount: data.rate,
          transitTime: data.transitTime || '3-7 days',
        }]);
      } else {
        setBomFreightError(data.error || 'No quotes available');
      }
    } catch (err: any) {
      console.error('BOM freight quote error:', err);
      setBomFreightError(err.message || 'Failed to get freight quotes');
    } finally {
      setBomFreightLoading(false);
    }
  };

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [groupsData, itemsData, settingsData, productLinesData, catalogData] = await Promise.all([
          api.inventory.getInventoryGroups(),
          api.inventory.getAll({ limit: 10000 }),
          api.systemSettings.getPowderCoatCharge().catch(() => ({ powder_coat_charge: 495 })),
          api.inventory.getProductLinesWithVersions().catch(() => []),
          api.fabricSettings.getCatalog().catch(() => ({ fabricTypes: [] })),
        ]);
        setGroups(groupsData || []);
        setItems(itemsData || []);
        setProductLines(productLinesData || []);
        if (catalogData?.fabricTypes?.length > 0) {
          setFabricCatalog(catalogData.fabricTypes);
        }
        if (settingsData?.powder_coat_charge) {
          setPowderCoatCharge(settingsData.powder_coat_charge);
        }
        // Also fetch saved BOMs and Full Screen Unit
        fetchSavedBoms();
        // Pass itemsData directly since state isn't updated yet
        loadFullScreenBom(itemsData || []);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Build assemblies list from groups
  const assemblies: Assembly[] = useMemo(() => {
    const assemblyGroups = groups
      .filter(g => g.isActive && !['Packaging', 'Accessories', 'Fabric', 'Fabric Assembly'].includes(g.name))
      .map(g => ({
        id: g.id,
        name: g.name,
        description: g.description || '',
        itemCount: g.itemCount,
      }));

    // Add "Full Screen Unit" option (independent BOM, not computed from other assemblies)
    return [
      {
        id: 'custom-build',
        name: 'Custom Build',
        description: 'Select individual items from inventory',
        itemCount: customBomItems.length,
        isCustom: true,
      },
      {
        id: 'full-screen',
        name: 'Full Screen Unit',
        description: 'Complete V-Track screen - independent BOM',
        itemCount: fullScreenBomItems.length,
        isFullScreen: true,
      },
      {
        id: 'fabric-panel',
        name: 'Fabric Panel (Assembly)',
        description: 'Fabric material + keder splines + corner patches — complete panel replacement BOM',
        itemCount: 5, // Base: fabric + 2 splines + 2 patches
        isFabricPanel: true,
      },
      {
        id: 'fabric-only',
        name: 'Fabric Only',
        description: 'Raw fabric material only — no splines or patches',
        itemCount: 1,
        isFabricOnly: true,
      },
      ...assemblyGroups,
    ];
  }, [groups, customBomItems.length, fullScreenBomItems.length]);

  // Helper function to check if an item matches the selected options
/**
 * Colour-keyword match on a WORD boundary.
 *
 * Plain `.includes()` was matching the wrong parts: every housing extrusion is
 * described "Housing Main  Standard 5.75\" Black 24'", and **"S-tan-dard"
 * contains "tan"** — so selecting Tan matched Black, White, Bronze and Mill
 * housings too. The /bom Full Screen Unit then listed all five housing mains
 * and all five covers, inflating its cost by ~$1,030 against the real
 * per-order BOM. V-Track and Bottom Bar were unaffected only because their
 * descriptions have no "Standard" in them.
 */
const colorKeywordMatches = (text: string, keyword: string): boolean => {
  return new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(text);
};

  const itemMatchesOptions = (item: InventoryItem, opts: BomOptions): boolean => {
    const i = item as any;
    const partNumber = (i.partNumber || i.part_number || '').toLowerCase();
    const description = (item.description || '').toLowerCase();
    const combined = `${partNumber} ${description}`;

    // Color keywords mapping
    const colorKeywords: Record<string, string[]> = {
      black: ['black', 'blk'],
      white: ['white', 'wht'],
      bronze: ['bronze', 'brz'],
      tan: ['tan', 'beige'],
    };

    // Check if item is color-specific (extrusions, tracks, housing, etc.)
    const isColorSpecific =
      combined.includes('track') ||
      combined.includes('housing') ||
      combined.includes('bottom bar') ||
      combined.includes('u-channel') ||
      combined.includes('weight bar') ||
      combined.includes('extrusion');

    // If color-specific, must match selected color OR be mill finish (used for custom powder coat)
    if (isColorSpecific) {
      // For custom color, use mill finish items
      if (opts.frameColor === 'custom' || opts.frameColor === 'mill') {
        const isMillFinish = combined.includes('mill finish') || combined.includes('mill');
        const isColorNeutral = !Object.values(colorKeywords).flat().some(kw => colorKeywordMatches(combined, kw));
        if (!isMillFinish && !isColorNeutral) {
          return false;
        }
      } else {
        const selectedColorKeywords = colorKeywords[opts.frameColor] || [opts.frameColor];
        const hasSelectedColor = selectedColorKeywords.some(kw => colorKeywordMatches(combined, kw));
        const isMillFinish = combined.includes('mill finish') || combined.includes('mill');
        const isColorNeutral = !Object.values(colorKeywords).flat().some(kw => colorKeywordMatches(combined, kw)) && !isMillFinish;

        if (!hasSelectedColor && !isColorNeutral) {
          return false;
        }
      }
    }

    // Check color-based applies_to conditions
    const appliesTo = i.appliesTo || i.applies_to || [];
    if (appliesTo.length > 0) {
      const hasColorBlack = appliesTo.includes('color_black');
      const hasColorWhite = appliesTo.includes('color_white');
      const hasColorBronze = appliesTo.includes('color_bronze');
      const hasColorTan = appliesTo.includes('color_tan');
      const hasColorMill = appliesTo.includes('color_mill_finish');
      const hasColorCustom = appliesTo.includes('color_custom');
      const hasColorCondition = hasColorBlack || hasColorWhite || hasColorBronze || hasColorTan || hasColorMill || hasColorCustom;

      if (hasColorCondition) {
        const colorMatches =
          (opts.frameColor === 'black' && hasColorBlack) ||
          (opts.frameColor === 'white' && hasColorWhite) ||
          (opts.frameColor === 'bronze' && hasColorBronze) ||
          (opts.frameColor === 'tan' && hasColorTan) ||
          (opts.frameColor === 'mill' && hasColorMill) ||
          (opts.frameColor === 'custom' && (hasColorCustom || hasColorMill));

        if (!colorMatches) return false;
      }
    }

    // Check System Type - V-Track (Housing) vs Open Roll
    const isHousingItem = combined.includes('housing') ||
                          combined.includes('end plate') ||
                          combined.includes('idler bracket x (1) custom') ||
                          combined.includes('starhead motor bracket') ||
                          (combined.includes('pile brush') && combined.includes('box'));
    // Group/component type are the authoritative signals — matching on the
    // description alone missed 13104 ("Screws- x (8) Holds Plates to Bracket"),
    // which says nothing about open roll but lives in "Open Roll Assembly", so
    // it was costed into every Housing Box screen here (+$1.20) while the
    // backend correctly dropped it. Same fields the backend filters on
    // (bom.service.ts processInventoryItemForBom).
    const invGroup = (i.inventoryGroup || i.inventory_group || '').toLowerCase();
    const compType = (i.componentType || i.component_type || '').toLowerCase();
    const isOpenRollItem = combined.includes('open roll')
                        || combined.includes('open-roll')
                        || invGroup.includes('open roll');

    if (opts.systemType === 'v_track') {
      // V-Track: exclude Open Roll parts (13101-13104)
      if (isOpenRollItem) return false;
    } else if (opts.systemType === 'open_roll') {
      // Open Roll: exclude Housing Assembly parts (12101-12107). Group and
      // component_type catch the housing extrusions (11126-11137), which live
      // in "Extrusions" and were missed by the description-only check.
      if (isHousingItem
          || invGroup.includes('housing')
          || compType === 'housing'
          || compType === 'housing cover') return false;
    }

    // Check fabric - match ONLY the selected fabric by part number
    const rawPartNumber = i.partNumber || i.part_number || '';
    const isFabricPart = rawPartNumber.startsWith('21'); // All fabric items are 21xxx

    if (isFabricPart) {
      // Get the expected part number for the selected fabric type + color
      const selectedFabricPartNumber = fabricPartMap[opts.fabricType]?.[opts.fabricColor];

      // Only include if this is the exact selected fabric
      if (rawPartNumber !== selectedFabricPartNumber) {
        return false;
      }
    }

    // Generic fabric items (splines, patches) should pass through
    const isGenericFabricItem = combined.includes('keder spline') ||
                                combined.includes('screen material') ||
                                combined.includes('patch');

    // Check motor type
    const isMotorItem =
      combined.includes('motor') ||
      combined.includes('alpha') ||
      combined.includes('somfy') ||
      combined.includes('keder motor');

    if (isMotorItem) {
      if (opts.motorType === 'none') {
        // No motor selected - exclude motor items
        if (combined.includes('alpha motor') || combined.includes('somfy motor') || combined.includes('keder motor')) {
          return false;
        }
      } else if (opts.motorType === 'keder') {
        // Keder selected - exclude Alpha and Somfy
        if (combined.includes('alpha motor') || combined.includes('somfy')) {
          return false;
        }
      } else if (opts.motorType === 'alpha') {
        // Alpha selected - exclude Somfy and Keder
        if (combined.includes('somfy') || combined.includes('keder motor')) {
          return false;
        }
      } else if (opts.motorType === 'somfy') {
        // Somfy selected - exclude Alpha and Keder
        if (combined.includes('alpha motor') || combined.includes('keder motor')) {
          return false;
        }
      }
    }

    // Remote — when a remote type is picked in the dropdown, only that exact
    // SKU passes. Without this every remote of the matching motor type would
    // come through (e.g. all 4 Alpha Pioneer remotes when only one is wanted).
    const REMOTE_TYPE_TO_PART: Record<string, string> = {
      'keder-1ch': '41143',
      'keder-16ch': '41143',
      'alpha-pioneer-1ch': '41101',
      'alpha-pioneer-4ch': '41102',
      'alpha-pioneer-8ch-lcd': '41103',
      'alpha-pioneer-16ch-lcd': '41104',
      'situo-1ch': '41109',
      'situo-5ch': '41110',
      'telis-1ch': '41111',
      'telis-4ch': '41112',
      'telis-16ch': '41113',
    };
    const componentType = String((i as any).componentType || (i as any).component_type || '').toLowerCase();
    const isRemoteItem = componentType === 'remote' || /\bremote\b/.test(description);
    if (isRemoteItem) {
      const wanted = opts.remoteType ? REMOTE_TYPE_TO_PART[opts.remoteType] : null;
      if (!wanted) return false;
      if (rawPartNumber !== wanted) return false;
    }

    // Check U-Channel - only include if uChannel is set
    const isUChannelItem = combined.includes('u-channel') || combined.includes('uchannel');
    const calcUChannelQty = calculateUChannelQty(opts.uChannel);
    if (isUChannelItem && calcUChannelQty === 0) {
      return false;
    }

    // Check Screw Covers - only include if screwCover is set
    const isScrewCoverItem = combined.includes('screw cover');
    const calcScrewCoverQty = calculateScrewCoverQty(opts.screwCover);
    if (isScrewCoverItem && calcScrewCoverQty === 0) {
      return false;
    }

    // Check shipping method - filter based on applies_to shipping conditions
    // (appliesTo already defined earlier in color check section)
    // Check if item has shipping-specific conditions
    const hasWillCall = appliesTo.includes('shipping_will_call');
    const hasFreight = appliesTo.includes('shipping_freight');
    const hasShippingCondition = hasWillCall || hasFreight;

    // If item has shipping conditions, must match current shipping method
    // Will Call → skip boxes, skids, pallets (items marked shipping_freight only)
    // Freight Shipping → include boxes, skids, pallets (items marked shipping_freight)
    if (hasShippingCondition) {
      if (opts.shippingMethod === 'will_call') {
        // Will Call: only include items with shipping_will_call
        if (!hasWillCall) return false;
      } else if (opts.shippingMethod === 'freight') {
        // Freight Shipping: include items with shipping_freight
        if (!hasFreight) return false;
      }
    }
    // Items with 'all' or no shipping conditions pass through

    // Check Brush Size - filter based on applies_to brush conditions
    const hasBrush1Inch = appliesTo.includes('brush_1_inch');
    const hasBrush2Inch = appliesTo.includes('brush_2_inch');
    const hasBrushCondition = hasBrush1Inch || hasBrush2Inch;

    if (hasBrushCondition) {
      // Brush Size = "1" → include brush_1_inch items only
      // Brush Size = "2" → include brush_2_inch items only
      if (opts.brushSize === '1' && !hasBrush1Inch) return false;
      if (opts.brushSize === '2' && !hasBrush2Inch) return false;
    }

    return true;
  };

  // Get items for selected assembly
  const assemblyItems = useMemo(() => {
    if (!selectedAssembly) return [];

    if (selectedAssembly.isCustom) {
      return []; // Custom build uses customBomItems instead
    }

    let filteredItems: InventoryItem[] = [];

    if (selectedAssembly.isFullScreen) {
      // Full Screen Unit uses its own independent item list
      filteredItems = items.filter(item => fullScreenBomItems.includes(item.id));
    } else if (selectedAssembly.isFabricOnly) {
      // Fabric Only: just the raw fabric material for the selected type/color
      const selectedFabricPartNumber = fabricPartMap[options.fabricType]?.[options.fabricColor];
      if (options.windowPanel) {
        // Window panel: Strataglass only
        filteredItems = items.filter(item => {
          const partNumber = (item as any).partNumber || (item as any).part_number || '';
          return partNumber === '21146';
        });
      } else {
        filteredItems = items.filter(item => {
          const group = (item as any).inventoryGroup || item.inventory_group;
          const partNumber = (item as any).partNumber || (item as any).part_number || '';
          return group === 'Fabric' && partNumber === selectedFabricPartNumber;
        });
      }
    } else if (selectedAssembly.isFabricPanel) {
      // Fabric Panel: assembly parts from "Fabric Assembly" group + selected fabric from "Fabric" group
      // Shows: keder splines (11310, 11311), corner patches (11313, 11314), selected fabric
      // When window panel enabled: also include Strataglass (21146)
      const assemblyPartNumbers = ['11310', '11311', '11313', '11314']; // Always included
      const selectedFabricPartNumber = fabricPartMap[options.fabricType]?.[options.fabricColor];

      // Add Strataglass for window panels
      if (options.windowPanel) {
        assemblyPartNumbers.push('21146'); // Strataglass
      }

      filteredItems = items.filter(item => {
        const group = (item as any).inventoryGroup || item.inventory_group;
        const partNumber = (item as any).partNumber || (item as any).part_number || '';

        // Assembly parts (splines, patches) come from "Fabric Assembly" group
        if (group === 'Fabric Assembly') {
          return assemblyPartNumbers.includes(partNumber);
        }

        // Raw fabric comes from "Fabric" group — only include the selected type/color
        if (group === 'Fabric') {
          return partNumber === selectedFabricPartNumber;
        }

        return false;
      });
    } else if ((selectedAssembly.name || '').toLowerCase() === 'track assembly') {
      // A track assembly is not just its hardware. inventory_group doubles as a
      // STOCKING category (all extrusions filed together) and as assembly
      // membership, and a part can only sit in one group — so the V-Track the
      // assembly is built around, and the springs that install into it, live
      // under "Extrusions" and "Spring Assembly" and never appeared here. The
      // view listed 10 screws/plates/stoppers and nothing you could actually
      // build a track from.
      const V_TRACK = new Set(['11101', '11102', '11103', '11104', '11106']);
      const INTERNAL_GUIDE = new Set(['11119']);
      const U_CHANNEL = new Set(['11113', '11114', '11115', '11116', '11118']);
      const SCREW_COVER = new Set(['11107', '11108', '11109', '11110', '11112']);

      filteredItems = items.filter(item => {
        const group = (item as any).inventoryGroup || item.inventory_group;
        const pn = (item as any).partNumber || (item as any).part_number || '';
        if (group === 'Track Assembly' || group === 'Spring Assembly') return true;
        if (V_TRACK.has(pn) || INTERNAL_GUIDE.has(pn)) return true;
        // Optional per the Track Options panel — only include when selected.
        if (U_CHANNEL.has(pn)) return options.uChannel !== 'none';
        if (SCREW_COVER.has(pn)) return options.screwCover !== 'none';
        return false;
      });
    } else {
      // Get items for specific assembly (by inventory_group)
      filteredItems = items.filter(item => {
        const group = (item as any).inventoryGroup || item.inventory_group;
        return group === selectedAssembly.name;
      });
    }

    // Apply option filters (color, fabric, motor, u-channel)
    // For Full Screen Unit: apply all filters including systemType
    // For Fabric Panel: already filtered above, just return as-is
    // For individual assemblies: skip systemType filter (they show their own group's items)
    if (selectedAssembly.isFullScreen) {
      return filteredItems.filter(item => itemMatchesOptions(item, options));
    } else if (selectedAssembly.isFabricPanel || selectedAssembly.isFabricOnly) {
      // Fabric items are already filtered correctly above
      return filteredItems;
    } else {
      // For individual assemblies, only apply color filtering if relevant
      // Skip systemType filter - the assembly already determines what items to show
      return filteredItems.filter(item => {
        const i = item as any;
        const combined = `${i.partNumber || i.part_number || ''} ${item.description || ''}`.toLowerCase();

        // Check if item is color-specific
        const isColorSpecific =
          combined.includes('track') ||
          combined.includes('housing') ||
          combined.includes('bottom bar') ||
          combined.includes('u-channel') ||
          combined.includes('weight bar') ||
          combined.includes('extrusion');

        if (isColorSpecific) {
          const colorKeywords: Record<string, string[]> = {
            black: ['black', 'blk'],
            white: ['white', 'wht'],
            bronze: ['bronze', 'brz'],
            tan: ['tan', 'beige'],
          };

          if (options.frameColor === 'custom' || options.frameColor === 'mill') {
            const isMillFinish = combined.includes('mill finish') || combined.includes('mill');
            const isColorNeutral = !Object.values(colorKeywords).flat().some(kw => colorKeywordMatches(combined, kw));
            if (!isMillFinish && !isColorNeutral) return false;
          } else {
            const selectedColorKeywords = colorKeywords[options.frameColor] || [options.frameColor];
            const hasSelectedColor = selectedColorKeywords.some(kw => colorKeywordMatches(combined, kw));
            const isMillFinish = combined.includes('mill finish') || combined.includes('mill');
            const isColorNeutral = !Object.values(colorKeywords).flat().some(kw => colorKeywordMatches(combined, kw)) && !isMillFinish;
            if (!hasSelectedColor && !isColorNeutral) return false;
          }
        }

        return true;
      });
    }
  }, [selectedAssembly, items, options, fullScreenBomItems]);

  // Filter items for picker
  const pickerItems = useMemo(() => {
    let filtered = items;

    // Filter by group
    if (selectedGroup) {
      filtered = filtered.filter(item => {
        const group = (item as any).inventoryGroup || item.inventory_group;
        return group === selectedGroup;
      });
    }

    // Filter by search
    if (itemSearch.trim()) {
      const search = itemSearch.toLowerCase();
      filtered = filtered.filter(item => {
        const i = item as any;
        const partNumber = i.partNumber || i.part_number || '';
        return (
          partNumber.toLowerCase().includes(search) ||
          (item.description || '').toLowerCase().includes(search)
        );
      });
    }

    return filtered.slice(0, 50); // Limit for performance
  }, [items, selectedGroup, itemSearch]);

  // Add item to custom BOM with formula evaluation
  const addToCustomBom = (item: InventoryItem) => {
    const i = item as any;
    const partNumber = i.partNumber || i.part_number || '';

    // Check if already added
    if (customBomItems.some(c => c.id === item.id)) {
      return;
    }

    // Build variables for formula evaluation
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;
    const vars: BomVariables = {
      W_system: w,
      boxW: w - (options.applyBoxDeduction ? 0.25 : 0),
      u_channel_ded: options.applyUChannelDeduction ? uChannelQty * 0.375 : 0,
      H_system: h,
      holes: calculateHoles(h),
      seam_count: calculateSeamCount(h),
      u_channel: options.uChannel !== 'none',
      u_channel_qty: options.uChannel === 'both' ? 2 : (options.uChannel !== 'none' ? 1 : 0),
      screw_cover: options.screwCover !== 'none',
      screw_cover_qty: options.screwCover === 'both' ? 2 : (options.screwCover !== 'none' ? 1 : 0),
      brush_size: parseFloat(options.brushSize) || 1,
      shipping_method: options.shippingMethod,
      biased_cut: false,
      window_panel: options.windowPanel,
      motor_type: options.motorType,
      remote_qty: options.motorType === 'none' ? 0 : Math.max(0, Number(options.remoteQty ?? 0)),
      remote_type: options.remoteType || '',
    };

    // Evaluate quantity formula if present
    const quantityFormula = i.quantityFormula || i.quantity_formula || '';
    let quantity = 1;
    if (quantityFormula && (w > 0 || h > 0)) {
      const calculated = evaluateFormula(quantityFormula, vars);
      if (calculated !== null && calculated > 0) {
        quantity = calculated;
      }
    }

    // Evaluate cut formula if present
    const cutFormula = i.cutFormula || i.cut_formula || '';
    let cutSize: number | undefined;
    if (cutFormula && (w > 0 || h > 0)) {
      const calculated = evaluateFormula(cutFormula, vars);
      if (calculated !== null && calculated > 0) {
        cutSize = Math.round(calculated * 100) / 100; // Round to 2 decimals
      }
    }

    // Get stock length, extrusion flag, and stock take type
    const stockLength = parseFloat(i.stockLength || i.stock_length || '0') || 0;
    const isExtrusion = i.isExtrusion || i.is_extrusion || false;
    const stockTakeType = i.stockTakeType || i.stock_take_type || '';

    setCustomBomItems(prev => [...prev, {
      id: item.id || '',
      partNumber,
      description: item.description || '',
      quantity,
      unitCost: (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0),
      inventoryGroup: i.inventoryGroup || i.inventory_group,
      cutSize,
      stockLength,
      isExtrusion,
      stockTakeType,
    }]);
  };

  // Remove item from custom BOM
  const removeFromCustomBom = (id: string) => {
    setCustomBomItems(prev => prev.filter(item => item.id !== id));
  };

  // Update quantity in custom BOM
  const updateCustomBomQuantity = (id: string, quantity: number) => {
    setCustomBomItems(prev => prev.map(item =>
      item.id === id ? { ...item, quantity: Math.max(0, quantity) } : item
    ));
  };

  // Multi-Assembly Build functions
  const addAssemblyToBuild = () => {
    if (!selectedAssembly || selectedAssembly.isCustom) return;

    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    // Get the current assembly items with their calculated values
    const buildItems = assemblyItems.map(item => {
      const i = item as any;
      const partNumber = i.partNumber || i.part_number || '';
      const unitCost = (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0);

      // Calculate quantity and cut length based on formulas
      let quantity = 1;
      let cutLength: number | undefined;

      const qtyFormula = i.quantityFormula || i.quantity_formula;
      const cutFormula = i.cutFormula || i.cut_formula;

      if (qtyFormula) {
        const result = evaluateFormula(qtyFormula, w, h);
        if (result !== null) quantity = Math.ceil(result);
      }

      if (cutFormula) {
        const result = evaluateFormula(cutFormula, w, h);
        if (result !== null) cutLength = result;
      }

      return {
        id: item.id || '',
        partNumber,
        description: item.description || '',
        quantity,
        unitCost,
        cutLength,
      };
    });

    const newBuildAssembly: BuildAssembly = {
      id: `${selectedAssembly.name}-${Date.now()}`,
      assemblyName: selectedAssembly.name,
      width: w,
      height: h,
      frameColor: options.frameColor,
      fabricType: options.fabricType,
      fabricColor: options.fabricColor,
      motorType: options.motorType,
      items: buildItems,
    };

    setMultiBuildItems(prev => [...prev, newBuildAssembly]);
    setShowMultiBuild(true);

    // Reset selection for next assembly
    setSelectedAssembly(null);
    setWidth('');
    setHeight('');
  };

  const removeAssemblyFromBuild = (buildId: string) => {
    setMultiBuildItems(prev => prev.filter(b => b.id !== buildId));
  };

  const clearMultiBuild = () => {
    setMultiBuildItems([]);
    setShowMultiBuild(false);
  };

  // Calculate combined totals for multi-build
  const multiBuildTotal = useMemo(() => {
    return multiBuildItems.reduce((total, assembly) => {
      const assemblyTotal = assembly.items.reduce((sum, item) => {
        return sum + (item.quantity * item.unitCost);
      }, 0);
      return total + assemblyTotal;
    }, 0);
  }, [multiBuildItems]);

  const multiBuildPartCount = useMemo(() => {
    return multiBuildItems.reduce((count, assembly) => count + assembly.items.length, 0);
  }, [multiBuildItems]);

  // BOM Editing functions
  const toggleBomItemRemoved = (id: string) => {
    setEditedBomItems(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(id);
      if (existing) {
        newMap.set(id, { ...existing, removed: !existing.removed });
      } else {
        newMap.set(id, { removed: true });
      }
      return newMap;
    });
  };

  const updateBomItemQuantity = (id: string, quantity: number) => {
    setEditedBomItems(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(id);
      newMap.set(id, { ...existing, removed: existing?.removed || false, quantityOverride: quantity });
      return newMap;
    });
  };

  const addItemToBomEdit = (item: InventoryItem) => {
    const i = item as any;
    const partNumber = i.partNumber || i.part_number || '';

    // Check if already added
    if (addedBomItems.some(c => c.id === item.id)) {
      return;
    }

    setAddedBomItems(prev => [...prev, {
      id: item.id || '',
      partNumber,
      description: item.description || '',
      quantity: 1,
      unitCost: (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0),
      inventoryGroup: i.inventoryGroup || i.inventory_group,
    }]);
  };

  const removeAddedBomItem = (id: string) => {
    setAddedBomItems(prev => prev.filter(item => item.id !== id));
  };

  const updateAddedBomItemQuantity = (id: string, quantity: number) => {
    setAddedBomItems(prev => prev.map(item =>
      item.id === id ? { ...item, quantity: Math.max(0, quantity) } : item
    ));
  };

  const resetBomEdits = () => {
    setIsEditingBom(false);
    setEditedBomItems(new Map());
    setAddedBomItems([]);
  };

  // Save custom BOM to database
  const saveAsCustomBom = async () => {
    if (!customBomName.trim()) return;

    setSavingCustomBom(true);
    try {
      // Build the items list from current BOM + edits
      const finalItems = [
        // Original BOM items (filtered by edits)
        ...bomLines
          .filter(line => {
            const edit = editedBomItems.get(line.id);
            return !edit?.removed;
          })
          .map(line => {
            const edit = editedBomItems.get(line.id);
            return {
              inventoryItemId: line.id,
              partNumber: line.partNumber,
              description: line.description,
              quantity: edit?.quantityOverride ?? line.quantity,
              unitCost: line.unitCost,
              cutLength: line.cutLength,
            };
          }),
        // Added items
        ...addedBomItems.map(item => ({
          inventoryItemId: item.id,
          partNumber: item.partNumber,
          description: item.description,
          quantity: item.quantity,
          unitCost: item.unitCost,
        })),
      ];

      await api.post('/bom/custom', {
        name: customBomName,
        assemblyId: selectedAssembly?.id,
        assemblyName: selectedAssembly?.name,
        width: parseFloat(width) || 0,
        height: parseFloat(height) || 0,
        options: options,
        items: finalItems,
        totalCost: editedBomTotal,
      });

      setShowSaveModal(false);
      setCustomBomName('');
      resetBomEdits();
      // Refresh saved BOMs list
      fetchSavedBoms();
      alert('Custom BOM saved successfully!');
    } catch (error) {
      console.error('Failed to save custom BOM:', error);
      alert('Failed to save custom BOM. Please try again.');
    } finally {
      setSavingCustomBom(false);
    }
  };

  // Fetch list of saved BOMs
  const fetchSavedBoms = async () => {
    setLoadingSavedBoms(true);
    try {
      const response = await api.get('/bom/custom/list');
      setSavedBoms(response || []);
    } catch (error) {
      console.error('Failed to fetch saved BOMs:', error);
    } finally {
      setLoadingSavedBoms(false);
    }
  };

  // Load a saved BOM into the calculator
  const loadSavedBom = async (bomId: string) => {
    try {
      const bom = await api.get(`/bom/custom/${bomId}`);
      if (!bom) return;

      // Set dimensions
      setWidth(String(bom.width || ''));
      setHeight(String(bom.height || ''));

      // Set options if available
      if (bom.options) {
        setOptions(prev => ({
          ...prev,
          ...bom.options,
        }));
      }

      // If this BOM has items, load them as custom BOM items
      if (bom.items && bom.items.length > 0) {
        setCustomBomItems(bom.items.map((item: any) => ({
          id: item.inventoryItemId || item.itemId || item.id,
          partNumber: item.partNumber,
          description: item.description,
          quantity: item.quantity,
          unitCost: item.unitCost,
          inventoryGroup: item.inventoryGroup,
        })));
        // Select custom build
        const customAssembly = assemblies.find(a => a.isCustom);
        if (customAssembly) {
          setSelectedAssembly(customAssembly);
        }
      }

      // Track that we loaded this BOM
      setLoadedSavedBomId(bomId);
      setCustomBomName(bom.name);
      setShowSavedBomsModal(false);
    } catch (error) {
      console.error('Failed to load saved BOM:', error);
      alert('Failed to load saved BOM. Please try again.');
    }
  };

  // Sales kits are fixed BOMs (no dimensions). Selecting the "Sales Kit" assembly
  // loads the New Dealer Starter Kit template directly instead of asking for W×H.
  const handleAssemblyClick = (assembly: Assembly) => {
    if (assembly.name === 'Sales Kit') {
      const kitTmpl = savedBoms.find(b => b.name === 'New Dealer Starter Kit');
      if (kitTmpl) { loadSavedBom(kitTmpl.id); return; }
    }
    setSelectedAssembly(assembly);
  };

  // Rename a saved BOM
  const renameSavedBom = async (bomId: string, newName: string) => {
    if (!newName.trim()) return;
    try {
      await api.patch(`/bom/custom/${bomId}`, { name: newName.trim() });
      setEditingBomId(null);
      setEditingBomName('');
      // Update local state
      setSavedBoms(prev => prev.map(bom =>
        bom.id === bomId ? { ...bom, name: newName.trim() } : bom
      ));
      // Update custom BOM name if this is the loaded one
      if (loadedSavedBomId === bomId) {
        setCustomBomName(newName.trim());
      }
    } catch (error) {
      console.error('Failed to rename BOM:', error);
      alert('Failed to rename BOM. Please try again.');
    }
  };

  // Update the currently loaded BOM (save changes to existing)
  const updateLoadedBom = async () => {
    if (!loadedSavedBomId) return;

    setSavingCustomBom(true);
    try {
      const finalItems = selectedAssembly?.isCustom
        ? customBomItems.map(item => ({
            inventoryItemId: item.id,
            partNumber: item.partNumber,
            description: item.description,
            quantity: item.quantity,
            unitCost: item.unitCost,
          }))
        : [
            ...bomLines
              .filter(line => {
                const edit = editedBomItems.get(line.id);
                return !edit?.removed;
              })
              .map(line => {
                const edit = editedBomItems.get(line.id);
                return {
                  inventoryItemId: line.id,
                  partNumber: line.partNumber,
                  description: line.description,
                  quantity: edit?.quantityOverride ?? line.quantity,
                  unitCost: line.unitCost,
                  cutLength: line.cutLength,
                };
              }),
            ...addedBomItems.map(item => ({
              inventoryItemId: item.id,
              partNumber: item.partNumber,
              description: item.description,
              quantity: item.quantity,
              unitCost: item.unitCost,
            })),
          ];

      await api.patch(`/bom/custom/${loadedSavedBomId}`, {
        name: customBomName || undefined,
        width: parseFloat(width) || 0,
        height: parseFloat(height) || 0,
        options: options,
        items: finalItems,
        totalCost: selectedAssembly?.isCustom ? customBomTotal : editedBomTotal,
      });

      resetBomEdits();
      fetchSavedBoms();
      alert('BOM updated successfully!');
    } catch (error) {
      console.error('Failed to update BOM:', error);
      alert('Failed to update BOM. Please try again.');
    } finally {
      setSavingCustomBom(false);
    }
  };

  // Delete a saved BOM
  const deleteSavedBom = async (bomId: string) => {
    if (!confirm('Are you sure you want to delete this saved BOM?')) return;
    try {
      await api.delete(`/bom/custom/${bomId}`);
      setSavedBoms(prev => prev.filter(bom => bom.id !== bomId));
      if (loadedSavedBomId === bomId) {
        setLoadedSavedBomId(null);
        setCustomBomName('');
      }
    } catch (error) {
      console.error('Failed to delete BOM:', error);
      alert('Failed to delete BOM. Please try again.');
    }
  };

  // Load Full Screen Unit BOM (independent storage)
  const loadFullScreenBom = async (loadedItems: InventoryItem[]) => {
    try {
      const response = await api.get('/bom/custom/list');
      const fullScreenBom = (response || []).find((b: any) => b.name === FULL_SCREEN_BOM_NAME);
      if (fullScreenBom) {
        const details = await api.get(`/bom/custom/${fullScreenBom.id}`);
        if (details?.items && details.items.length > 0) {
          setFullScreenBomItems(details.items.map((i: any) => i.inventoryItemId || i.itemId || i.id));
        } else {
          // Saved BOM exists but has no items - initialize from current assemblies
          initializeFullScreenFromItems(loadedItems);
        }
      } else {
        // No saved BOM exists - initialize with all current BOM assembly items (one-time setup)
        initializeFullScreenFromItems(loadedItems);
      }
      setFullScreenBomLoaded(true);
    } catch (error) {
      console.error('Failed to load Full Screen BOM:', error);
      // On error, initialize from loaded items
      initializeFullScreenFromItems(loadedItems);
      setFullScreenBomLoaded(true);
    }
  };

  // Initialize Full Screen Unit from inventory items
  const initializeFullScreenFromItems = (loadedItems: InventoryItem[]) => {
    const excludeGroups = ['Packaging', 'Accessories', 'Sales Kit', 'Demo Units', 'Demo Components'];
    const initialItems = loadedItems
      .filter(item => {
        const group = (item as any).inventoryGroup || (item as any).inventory_group;
        return group && !excludeGroups.includes(group);
      })
      .map(item => item.id);
    setFullScreenBomItems(initialItems);
    // Save to database so it persists
    if (initialItems.length > 0) {
      saveFullScreenBom(initialItems);
    }
  };

  // Save Full Screen Unit BOM
  const saveFullScreenBom = async (itemIds: string[]) => {
    try {
      const itemDetails = itemIds.map(id => {
        const item = items.find(i => i.id === id);
        const i = item as any;
        return {
          inventoryItemId: id,
          partNumber: i?.partNumber || i?.part_number || '',
          description: item?.description || '',
          quantity: 1,
          unitCost: parseFloat(i?.unitCost || i?.unit_cost || i?.unit_price || '0') || 0,
        };
      });

      // Check if exists
      const response = await api.get('/bom/custom/list');
      const existing = (response || []).find((b: any) => b.name === FULL_SCREEN_BOM_NAME);

      if (existing) {
        await api.patch(`/bom/custom/${existing.id}`, {
          items: itemDetails,
          totalCost: itemDetails.reduce((sum, i) => sum + i.unitCost, 0),
        });
      } else {
        await api.post('/bom/custom', {
          name: FULL_SCREEN_BOM_NAME,
          assemblyName: 'Full Screen Unit',
          width: 0,
          height: 0,
          options: {},
          items: itemDetails,
          totalCost: itemDetails.reduce((sum, i) => sum + i.unitCost, 0),
        });
      }
    } catch (error) {
      console.error('Failed to save Full Screen BOM:', error);
    }
  };

  // Open assembly edit modal
  const openEditAssembly = (assembly: Assembly) => {
    if (assembly.isCustom) return;
    setEditingAssembly(assembly);
    setAssemblyItemSearch('');
    setShowEditAssemblyModal(true);
  };

  // Get items in the assembly being edited
  const editingAssemblyItems = useMemo(() => {
    if (!editingAssembly) return [];

    // For Full Screen Unit, use the independent fullScreenBomItems list (NOT linked to other assemblies)
    if (editingAssembly.isFullScreen) {
      return items.filter(item => fullScreenBomItems.includes(item.id));
    }

    return items.filter(item => {
      const group = (item as any).inventoryGroup || (item as any).inventory_group;
      return group === editingAssembly.name;
    });
  }, [editingAssembly, items, fullScreenBomItems]);

  // Get items NOT in the assembly (for adding)
  const availableItemsForAssembly = useMemo(() => {
    if (!editingAssembly) return [];
    const search = assemblyItemSearch.toLowerCase();

    return items
      .filter(item => {
        const i = item as any;
        const group = i.inventoryGroup || i.inventory_group;

        // For Full Screen Unit, show items NOT already in the Full Screen BOM
        if (editingAssembly.isFullScreen) {
          if (fullScreenBomItems.includes(item.id)) return false;
        } else {
          // Not in this specific assembly (by inventory_group)
          if (group === editingAssembly.name) return false;
        }

        // Match search
        if (search) {
          const partNumber = (i.partNumber || i.part_number || '').toLowerCase();
          const desc = (item.description || '').toLowerCase();
          return partNumber.includes(search) || desc.includes(search);
        }
        return true;
      })
      .slice(0, 50);
  }, [editingAssembly, items, assemblyItemSearch, fullScreenBomItems]);

  // Add item to assembly
  const addItemToAssembly = async (itemId: string) => {
    if (!editingAssembly) return;

    // For Full Screen Unit, just add to the independent list (no inventory_group change)
    if (editingAssembly.isFullScreen) {
      const newItems = [...fullScreenBomItems, itemId];
      setFullScreenBomItems(newItems);
      saveFullScreenBom(newItems); // Persist to database
      return;
    }

    // For other assemblies, update inventory_group
    setSavingAssemblyChanges(true);
    try {
      await api.inventory.update(itemId, { inventory_group: editingAssembly.name });
      // Update local state
      setItems(prev => prev.map(item =>
        item.id === itemId
          ? { ...item, inventoryGroup: editingAssembly.name, inventory_group: editingAssembly.name }
          : item
      ));
      // Update groups count
      setGroups(prev => prev.map(g =>
        g.name === editingAssembly.name
          ? { ...g, itemCount: (g.itemCount || 0) + 1 }
          : g
      ));
    } catch (error) {
      console.error('Failed to add item to assembly:', error);
      alert('Failed to add item to assembly.');
    } finally {
      setSavingAssemblyChanges(false);
    }
  };

  // Remove item from assembly
  const removeItemFromAssembly = async (itemId: string) => {
    if (!editingAssembly) return;

    // For Full Screen Unit, just remove from the independent list (no inventory_group change)
    if (editingAssembly.isFullScreen) {
      const newItems = fullScreenBomItems.filter(id => id !== itemId);
      setFullScreenBomItems(newItems);
      saveFullScreenBom(newItems); // Persist to database
      return;
    }

    // For other assemblies, clear inventory_group
    setSavingAssemblyChanges(true);
    try {
      await api.inventory.update(itemId, { inventory_group: '' });
      // Update local state
      setItems(prev => prev.map(i =>
        i.id === itemId
          ? { ...i, inventoryGroup: '', inventory_group: '' }
          : i
      ));
      // Update groups count
      setGroups(prev => prev.map(g =>
        g.name === editingAssembly.name
          ? { ...g, itemCount: Math.max(0, (g.itemCount || 0) - 1) }
          : g
      ));
    } catch (error) {
      console.error('Failed to remove item from assembly:', error);
      alert('Failed to remove item from assembly.');
    } finally {
      setSavingAssemblyChanges(false);
    }
  };

  // Calculate BOM
  // ---------------------------------------------------------------------------
  // Full Screen Unit is costed by the SERVER (BomService, via POST
  // /bom/calculate-bom) — the same engine that generates a released order's BOM
  // and cut sheet. It used to be recomputed here in the browser, which meant
  // every cut formula, deduction and proration rule existed twice and drifted:
  // non-black BOMs lost a track and a u-channel, widths ran 1" wide, Open Roll
  // parts landed on housing screens, and the motor was costed off list price
  // instead of landed. Those are fixed, but the only durable fix is one engine.
  //
  // The client-side calculation below is retained ONLY for browsing individual
  // assemblies (Track, Extrusions, ...), which the server endpoint does not
  // model. It is a viewer, not a second cost engine — if the server call fails
  // for Full Screen Unit we surface the error rather than silently falling back
  // to a different set of numbers.
  // ---------------------------------------------------------------------------
  const [serverBomLines, setServerBomLines] = useState<BomLineItem[] | null>(null);
  const [serverBomError, setServerBomError] = useState<string | null>(null);
  const [serverBomLoading, setServerBomLoading] = useState(false);

  useEffect(() => {
    if (!selectedAssembly?.isFullScreen) { setServerBomLines(null); setServerBomError(null); return; }
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;
    if (w <= 0 || h <= 0) { setServerBomLines(null); setServerBomError(null); return; }

    let cancelled = false;
    setServerBomLoading(true);
    setServerBomError(null);

    api.bom.calculateFullBom({
      // The page takes a single width/height; the server derives widthSelected
      // from three measured widths, so feed the same value to all three.
      widthTop: w, widthMiddle: w, widthBottom: w,
      heightLeft: h, heightCenter: h, heightRight: h,
      uChannelLeft: options.uChannel === 'left' || options.uChannel === 'both',
      uChannelRight: options.uChannel === 'right' || options.uChannel === 'both',
      applyUChannelDeductions: options.applyUChannelDeduction,
      applyBoxDeduction: options.applyBoxDeduction,
      motorType: options.motorType,
      brushSize: options.brushSize,
      fabricType: options.fabricType,
      fabricColor: options.fabricColor,
      systemColor: options.frameColor,
      systemType: options.systemType,
    })
      .then(res => {
        if (cancelled) return;
        setServerBomLines((res.bomItems || []).map((b: any) => ({
          id: b.inventoryItemId || b.partNumber,
          partNumber: b.partNumber,
          description: b.description || '',
          formula: b.quantityFormula || '',
          quantity: b.quantity,
          unitCost: b.unitCost || 0,
          totalCost: b.totalCost || 0,
          stockTakeType: b.stockTakeType,
          cutLength: b.cutLength,
          stockLength: b.stockLength,
          proratedCost: b.proratedCost,
          isExtrusion: b.isExtrusion,
          hasCutFormula: b.cutLength != null,
          inventoryGroup: b.inventoryGroup,
        })));
      })
      .catch(err => { if (!cancelled) { setServerBomLines(null); setServerBomError(err?.message || 'Failed to cost BOM'); } })
      .finally(() => { if (!cancelled) setServerBomLoading(false); });

    return () => { cancelled = true; };
  }, [selectedAssembly, width, height, options, uChannelQty, screwCoverQty]);

  const bomLines: BomLineItem[] = useMemo(() => {
    // Full Screen Unit: server numbers only, never a local recomputation.
    if (selectedAssembly?.isFullScreen) return serverBomLines || [];

    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    // Require only the dimensions this assembly actually collects. The guard used
    // to demand BOTH width and height, but the form renders each input from
    // `needsWidth` / `needsHeight` — so a height-only assembly (Track, Spring)
    // never gets a width, `w` stays 0, and the BOM silently returned []. Track
    // Assembly has therefore always shown an empty list with no error: the parts
    // count in the header comes from the assembly itself, not from these lines.
    const asmName = (selectedAssembly?.name || '').toLowerCase();
    const wantsWidth = !selectedAssembly
      || selectedAssembly.isFullScreen || selectedAssembly.isCustomBuild
      || selectedAssembly.isFabricPanel || selectedAssembly.isFabricOnly
      || ['weight bar', 'roller', 'housing', 'fabric', 'extrusion', 'open roll'].some(n => asmName.includes(n));
    const wantsHeight = !selectedAssembly
      || selectedAssembly.isFullScreen || selectedAssembly.isCustomBuild
      || selectedAssembly.isFabricPanel || selectedAssembly.isFabricOnly
      || ['track', 'fabric', 'extrusion', 'spring'].some(n => asmName.includes(n));

    if (assemblyItems.length === 0) return [];
    if (wantsWidth && w <= 0) return [];
    if (wantsHeight && h <= 0) return [];
    if (w <= 0 && h <= 0) return [];

    // Mirror BomService.calculateDimensions():
    //   systemWidth = widthSelected + boxWidthAdjustment - uChannelDeduction
    //   boxW        = widthSelected + boxWidthAdjustment - boxDeduction
    // boxWidthAdjustment is a track-mount value this page doesn't model, so it
    // is 0 here; enter the order's own width to match a released BOM exactly.
    const uChannelDed = options.applyUChannelDeduction ? uChannelQty * 0.375 : 0;
    const boxDed = options.applyBoxDeduction ? 0.25 : 0;
    const wSystem = w - uChannelDed;
    const boxWidth = w - boxDed;

    const vars: BomVariables = {
      W_system: wSystem,
      boxW: boxWidth,
      u_channel_ded: uChannelDed,
      H_system: h,
      holes: calculateHoles(h),
      seam_count: calculateSeamCount(h),
      u_channel: options.uChannel,
      u_channel_qty: uChannelQty,
      screw_cover: options.screwCover,
      screw_cover_qty: screwCoverQty,
      brush_size: parseFloat(options.brushSize),
      motor_type: options.motorType,
      fabric_type: options.fabricType,
      frame_color: options.frameColor,
      window_panel: options.windowPanel,
      window_count: options.windowPanel ? options.windowCount : 0,
      remote_qty: options.motorType === 'none' ? 0 : Math.max(0, Number(options.remoteQty ?? 0)),
      remote_type: options.remoteType || '',
    };

    // Cut size formulas from the spec
    const cutSizes: Record<string, number> = {
      vTrack: h,                          // H_system
      screwCover: h,                      // H_system
      uChannel: h,                        // H_system
      kederGuide: h,                      // H_system
      housingMain: boxWidth - 0.5,        // boxW - 0.5
      housingCover: boxWidth - 0.5625,    // boxW - 0.5625
      // wSystem already carries the u-channel deduction — subtracting it again
      // here double-deducted and cut the bar/fabric short of the cut sheet.
      weightBar: wSystem - 6.625,         // W_system - 6.625
      rollerTube: boxWidth - uChannelDed - 14.5, // boxW - u_ded - 14.5
      fabricWidth: wSystem - 3.5,         // W_system - 3.5
      fabricHeight: h + 10,               // H_system + 10
    };

    // Default stock lengths for extrusions
    const stockLengths: Record<string, number> = {
      roller: 288,        // 24' = 288"
      housing: 288,
      v_track: 324,       // 27' = 324"
      track: 324,
      weight_bar: 288,
      u_channel: 324,
      keder_guide: 324,
      screw_cover: 324,
    };

    // Helper to identify component type and calculate cut length from item
    // Priority: 1) Use cut_formula from database if exists, 2) Fall back to hardcoded mappings for extrusions
    const getComponentInfo = (i: any): { cutLength?: number; stockLength?: number; isExtrusion: boolean; hasCutFormula: boolean } => {
      const partNumber = (i.partNumber || i.part_number || '').toLowerCase();
      const description = (i.description || '').toLowerCase();
      const combined = `${partNumber} ${description}`;
      const isExtrusion = i.isExtrusion || i.is_extrusion || false;
      const cutFormula = i.cutFormula || i.cut_formula;
      const itemStockLength = parseFloat(i.stockLength || i.stock_length || '0');

      // If item has a cut_formula, evaluate it to get cut length
      if (cutFormula) {
        const calculatedCutLength = evaluateFormula(cutFormula, vars);
        if (calculatedCutLength !== null && calculatedCutLength > 0) {
          return {
            cutLength: calculatedCutLength,
            stockLength: itemStockLength > 0 ? itemStockLength : (isExtrusion ? 288 : undefined),
            isExtrusion,
            hasCutFormula: true,
          };
        }
      }

      // For non-extrusions without cut_formula, no cut length
      if (!isExtrusion) return { isExtrusion: false, hasCutFormula: false };

      // Map extrusions to cut sizes based on component type (fallback for items without cut_formula)
      if (combined.includes('v-track') || combined.includes('vtrack') || combined.includes('v track')) {
        return { cutLength: cutSizes.vTrack, stockLength: stockLengths.track, isExtrusion: true, hasCutFormula: false };
      }
      if (combined.includes('roller') || combined.includes('tube')) {
        return { cutLength: cutSizes.rollerTube, stockLength: stockLengths.roller, isExtrusion: true, hasCutFormula: false };
      }
      if (combined.includes('housing') && combined.includes('cover')) {
        return { cutLength: cutSizes.housingCover, stockLength: stockLengths.housing, isExtrusion: true, hasCutFormula: false };
      }
      if (combined.includes('housing')) {
        return { cutLength: cutSizes.housingMain, stockLength: stockLengths.housing, isExtrusion: true, hasCutFormula: false };
      }
      if (combined.includes('weight bar') || combined.includes('bottom bar')) {
        return { cutLength: cutSizes.weightBar, stockLength: stockLengths.weight_bar, isExtrusion: true, hasCutFormula: false };
      }
      if (combined.includes('u-channel') || combined.includes('uchannel')) {
        return { cutLength: cutSizes.uChannel, stockLength: stockLengths.u_channel, isExtrusion: true, hasCutFormula: false };
      }
      if (combined.includes('keder') && combined.includes('guide')) {
        return { cutLength: cutSizes.kederGuide, stockLength: stockLengths.keder_guide, isExtrusion: true, hasCutFormula: false };
      }
      if (combined.includes('screw cover')) {
        return { cutLength: cutSizes.screwCover, stockLength: stockLengths.screw_cover, isExtrusion: true, hasCutFormula: false };
      }

      // Use stock_length from item if available
      if (itemStockLength > 0) {
        return { stockLength: itemStockLength, isExtrusion: true, hasCutFormula: false };
      }

      return { isExtrusion: true, stockLength: 288, hasCutFormula: false }; // Default 24'
    };

    return assemblyItems
      .map(item => {
        const i = item as any;
        const stockTakeType = i.stockTakeType || i.stock_take_type;
        const quantityFormula = i.quantityFormula || i.quantity_formula;
        const rawUnitCost = (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0);

        let quantity = 0;
        let formula = '';

        // Determine quantity based on stock take type
        switch (stockTakeType) {
          case 'fixed':
            quantity = evaluateFormula(quantityFormula, vars) ?? 1;
            formula = quantityFormula || '1';
            break;
          case 'per_screen':
            quantity = evaluateFormula(quantityFormula, vars) ?? 1;
            formula = quantityFormula || '1 per screen';
            break;
          case 'calculated':
            quantity = evaluateFormula(quantityFormula, vars) ?? 0;
            formula = quantityFormula || '-';
            break;
          case 'cut_length':
            quantity = evaluateFormula(quantityFormula, vars) ?? 1;
            formula = quantityFormula || 'cut';
            break;
          case 'linear_from_roll':
            quantity = evaluateFormula(quantityFormula, vars) ?? 1;
            formula = quantityFormula || 'linear';
            break;
          // Colour-variant / conditional parts are stocked PER SIDE. The backend
          // multiplies the formula by how many sides match (checkAppliesTo's
          // quantityMultiplier); this page had no such case, so they fell to
          // `default` and were counted once — picking any colour but black
          // dropped one V-track and one u-channel from the estimate.
          //
          // Black looked right only by accident: its parts carry
          // stock_take_type='cut_length' with the side count baked into the
          // formula ('2', 'u_channel_qty'), so they never needed a multiplier.
          //
          // NOTE: duplicated backend logic. Delete when the BOM engines are
          // consolidated and this page asks the API instead of recomputing.
          case 'color_variant':
          case 'conditional': {
            const appliesTo: string[] = (i.appliesTo || i.applies_to || []).map((a: any) => String(a));
            const has = (k: string) => appliesTo.includes(k);
            let sides = 1;
            if (has('u_channel_left') || has('u_channel_right') || has('u_channel_both')) {
              sides = uChannelQty;
            } else if (has('screw_cover_left') || has('screw_cover_right')) {
              sides = screwCoverQty;
            } else if (has('left_side') && has('right_side')) {
              sides = 2;
            } else if (has('left_side') || has('right_side')) {
              sides = 1;
            }
            quantity = (evaluateFormula(quantityFormula, vars) ?? 1) * sides;
            formula = `${quantityFormula || '1'} × ${sides} side${sides === 1 ? '' : 's'}`;
            break;
          }
          default:
            quantity = quantityFormula ? (evaluateFormula(quantityFormula, vars) ?? 1) : 1;
            formula = quantityFormula || '1';
        }

        quantity = Math.round(quantity * 100) / 100;

        // Fabric Only: override quantity to 1 (seam formula is for production, not fabric sales)
        if (selectedAssembly?.isFabricOnly) {
          quantity = 1;
        }

        // Special handling for Strataglass (21146) as window panels
        // When used in Fabric Panel, quantity = windowCount
        const partNumber = i.partNumber || i.part_number || '';
        if (partNumber === '21146' && options.windowPanel && options.windowCount > 0) {
          quantity = options.windowCount;
        }

        // Get component info (cut length from formula or hardcoded mappings)
        const componentInfo = getComponentInfo(i);

        // Calculate prorated cost for items with cut length and stock length
        // This applies to BOTH extrusions AND linear_from_roll items (fabric, keder spline)
        // Formula: (cutLength / stockLength) × unitCost × qty
        let unitCost = rawUnitCost;
        let proratedCost: number | undefined;

        if (componentInfo.cutLength && componentInfo.stockLength && rawUnitCost > 0) {
          proratedCost = (componentInfo.cutLength / componentInfo.stockLength) * rawUnitCost;
          unitCost = proratedCost; // Use prorated cost for total calculation
        }

        return {
          id: item.id || '',
          partNumber: i.partNumber || i.part_number || '',
          description: item.description || '',
          formula,
          quantity,
          unitCost: rawUnitCost,
          totalCost: quantity * unitCost,
          stockTakeType,
          // Cut length fields (for extrusions AND items with cut_formula)
          cutLength: componentInfo.cutLength,
          stockLength: componentInfo.stockLength,
          proratedCost,
          isExtrusion: componentInfo.isExtrusion,
          hasCutFormula: componentInfo.hasCutFormula,
        };
      })
      .filter(line => line.quantity > 0)
      .sort((a, b) => a.partNumber.localeCompare(b.partNumber));
  }, [assemblyItems, width, height, options, uChannelQty, screwCoverQty, selectedAssembly, serverBomLines]);

  // Custom powder coating charge - uses value from system settings (mill finish requires powder coating)
  const powderCoatCost = options.frameColor === 'mill' ? powderCoatCharge : 0;

  // Calculate totals for standard BOM
  const totalBomCost = useMemo(() => {
    const partsTotal = bomLines.reduce((sum, line) => sum + line.totalCost, 0);
    return partsTotal + powderCoatCost;
  }, [bomLines, powderCoatCost]);

  // Calculate edited BOM total
  const editedBomTotal = useMemo(() => {
    if (!isEditingBom) return totalBomCost;

    const originalTotal = bomLines
      .filter(line => {
        const edit = editedBomItems.get(line.id);
        return !edit?.removed;
      })
      .reduce((sum, line) => {
        const edit = editedBomItems.get(line.id);
        const qty = edit?.quantityOverride ?? line.quantity;
        return sum + (qty * (line.proratedCost ?? line.unitCost));
      }, 0);

    const addedTotal = addedBomItems.reduce((sum, item) => sum + (item.quantity * item.unitCost), 0);

    return originalTotal + addedTotal;
  }, [isEditingBom, bomLines, editedBomItems, addedBomItems, totalBomCost]);

  // Calculate prorated cost for a custom BOM item
  const calculateItemCost = (item: CustomBomItem): number => {
    // For linear_from_roll items (fabric, keder spline): unitCost is per inch
    // Formula: qty × cutSize × unitCost
    if (item.stockTakeType === 'linear_from_roll' && item.cutSize && item.cutSize > 0) {
      return item.quantity * item.cutSize * item.unitCost;
    }
    // For items with stock length (extrusions, cut_length items like brush): prorate
    // Formula: qty × (cutSize / stockLength) × unitCost
    if (item.cutSize && item.stockLength && item.stockLength > 0) {
      return item.quantity * (item.cutSize / item.stockLength) * item.unitCost;
    }
    // For items without cut formulas or stock length: use full unit cost
    return item.quantity * item.unitCost;
  };

  // Calculate totals for custom BOM (with proration for extrusions)
  const customBomTotal = useMemo(() => {
    return customBomItems.reduce((sum, item) => sum + calculateItemCost(item), 0);
  }, [customBomItems]);

  // Get the active total based on selection
  const activeTotalCost = selectedAssembly?.isCustom ? customBomTotal : totalBomCost;

  const calculatedMsrp = useMemo(() => {
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    // Only the screen pricing matrix needs BOTH dimensions — it's a width x
    // height lookup. Individual assemblies price off BOM cost and need neither.
    // The width guard used to sit above this branch, so a height-only assembly
    // (Track, Spring) always returned null: MSRP read "Enter dimensions" and
    // Dealer Price sat at $0.00 even with a perfectly good BOM cost on screen.
    if (selectedAssembly?.isFullScreen) {
      if (w <= 0 || h <= 0) return null;
      const productType = fabricToProductType[options.fabricType] || 'solar94';
      return getScreenMsrp(productType, w, h);
    }

    // For non-full-screen assemblies: MSRP = BOM cost * 5 (400% markup)
    if (activeTotalCost > 0) {
      return Math.round(activeTotalCost * 5 * 100) / 100;
    }

    return null;
  }, [width, height, options.fabricType, selectedAssembly, activeTotalCost]);

  // Calculate margin - use override if provided, otherwise use calculated MSRP
  const marginCalc = useMemo(() => {
    const msrp = msrpOverride ? parseFloat(msrpOverride) : (calculatedMsrp || 0);
    const discount = parseFloat(dealerDiscount) || 0;
    const dealerPrice = msrp * (1 - discount / 100);
    const grossMargin = dealerPrice - activeTotalCost;
    const marginPercent = dealerPrice > 0 ? (grossMargin / dealerPrice) * 100 : 0;

    return {
      msrp,
      dealerPrice,
      grossMargin,
      marginPercent,
      isCalculated: !msrpOverride && calculatedMsrp !== null,
    };
  }, [msrpOverride, calculatedMsrp, dealerDiscount, activeTotalCost]);

  // Variables display
  const varsDisplay = useMemo(() => {
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;
    return {
      W_system: w,
      boxW: w - (options.applyBoxDeduction ? 0.25 : 0),
      u_channel_ded: options.applyUChannelDeduction ? uChannelQty * 0.375 : 0,
      H_system: h,
      holes: calculateHoles(h),
      seam_count: calculateSeamCount(h),
      u_channel: options.uChannel,
      u_channel_qty: uChannelQty,
      screw_cover: options.screwCover,
      screw_cover_qty: screwCoverQty,
      brush_size: parseFloat(options.brushSize),
      shipping_method: options.shippingMethod,
      motor_type: options.motorType,
      fabric_type: options.fabricType,
      frame_color: options.frameColor,
      window_panel: options.windowPanel,
      window_count: options.windowPanel ? options.windowCount : 0,
    };
  }, [width, height, options, uChannelQty, screwCoverQty]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Bill of Materials"
        subtitle="Calculate BOM costs for assemblies and screen units"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel: Assembly Selector */}
        <div className="lg:col-span-1">
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Select Assembly</h2>
              <button
                onClick={() => { fetchSavedBoms(); setShowSavedBomsModal(true); }}
                className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Saved BOMs
              </button>
            </div>
            {/* Loaded BOM indicator */}
            {loadedSavedBomId && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-xs font-medium text-blue-600">Currently Loaded:</span>
                    <p className="font-medium text-blue-900">{customBomName}</p>
                  </div>
                  <button
                    onClick={() => {
                      setLoadedSavedBomId(null);
                      setCustomBomName('');
                      setCustomBomItems([]);
                      resetBomEdits();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {assemblies.map(assembly => (
                <div
                  key={assembly.id}
                  className={clsx(
                    'p-4 rounded-lg border-2 transition-all',
                    selectedAssembly?.id === assembly.id
                      ? assembly.isCustom
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  )}
                >
                  <div className="flex justify-between items-start">
                    <button
                      onClick={() => handleAssemblyClick(assembly)}
                      className="flex-1 text-left"
                    >
                      <h3 className={clsx(
                        'font-medium',
                        assembly.isCustom ? 'text-purple-700' :
                        assembly.isFullScreen ? 'text-blue-700' : 'text-gray-900'
                      )}>
                        {assembly.isCustom && '🛠️ '}{assembly.name}
                      </h3>
                      {assembly.description && (
                        <p className="text-sm text-gray-500 mt-1">{assembly.description}</p>
                      )}
                    </button>
                    <div className="flex items-center gap-2 ml-2">
                      <span className={clsx(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        assembly.isCustom
                          ? 'bg-purple-100 text-purple-700'
                          : assembly.isFullScreen
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                      )}>
                        {assembly.itemCount} {assembly.isCustom ? 'selected' : 'parts'}
                      </span>
                      {!assembly.isCustom && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditAssembly(assembly); }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit assembly"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right Panel: BOM Calculator */}
        <div className="lg:col-span-2 space-y-6">
          {/* Inputs - show for all builds including custom (needs options for filtering logic) */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedAssembly?.name ? `${selectedAssembly.name} - Dimensions & Options` : 'Dimensions & Options'}
            </h2>

            {/* Assembly-specific field requirements */}
            {(() => {
              const assemblyName = selectedAssembly?.name?.toLowerCase() || '';
              const isFullScreen = selectedAssembly?.isFullScreen;
              const isFabricPanel = selectedAssembly?.isFabricPanel;
              const isFabricOnly = selectedAssembly?.isFabricOnly;
              const isCustomBuild = selectedAssembly?.isCustom;
              const isTrack = assemblyName.includes('track');
              const isWeightBar = assemblyName.includes('weight bar');
              const isRoller = assemblyName.includes('roller');
              const isHousing = assemblyName.includes('housing');
              const isFabric = assemblyName.includes('fabric') && !isFabricPanel && !isFabricOnly; // Raw fabric assembly
              const isExtrusion = assemblyName.includes('extrusion');
              const isOpenRoll = assemblyName.includes('open roll');
              const isSpring = assemblyName.includes('spring');

              // Determine which dimensions to show
              // Custom builds show all options for filtering logic
              // Open Roll only needs width (it's just the roller mechanism without housing)
              // Fabric Panel needs width & height for fabric + keder spline calculations
              const needsWidth = isFullScreen || isCustomBuild || isFabricPanel || isFabricOnly || isWeightBar || isRoller || isHousing || isFabric || isExtrusion || isOpenRoll || !selectedAssembly;
              const needsHeight = isFullScreen || isCustomBuild || isFabricPanel || isFabricOnly || isTrack || isFabric || isExtrusion || isSpring || !selectedAssembly;
              const needsColor = isFullScreen || isCustomBuild || isTrack || isWeightBar || isHousing || isExtrusion || !selectedAssembly;
              const needsFabric = isFullScreen || isCustomBuild || isFabricPanel || isFabricOnly || isFabric || !selectedAssembly;
              const needsMotor = isFullScreen || isCustomBuild || isRoller || !selectedAssembly;
              const needsFullOptions = isFullScreen || isCustomBuild || !selectedAssembly;
              // Track assembly needs U-channel and Screw Cover options
              const needsTrackOptions = isTrack || isCustomBuild;
              // Fabric Panel can have window panels
              const needsWindowOptions = isFullScreen || isCustomBuild || isFabricPanel || isFabricOnly;

                return (
                  <>
                    {/* Dimensions Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {needsWidth && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Width (inches) *
                          </label>
                          <input
                            type="number"
                            value={width}
                            onChange={e => setWidth(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="120"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                      {needsHeight && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Height (inches) *
                          </label>
                          <input
                            type="number"
                            value={height}
                            onChange={e => setHeight(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="120"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                      {needsColor && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Frame Color
                          </label>
                          <select
                            value={options.frameColor}
                            onChange={e => setOptions({ ...options, frameColor: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="black">Black</option>
                            <option value="white">White</option>
                            <option value="bronze">Bronze</option>
                            <option value="tan">Tan</option>
                            <option value="mill">Mill Finish</option>
                          </select>
                        </div>
                      )}
                    </div>

              {/* Track Options - U-Channel and Screw Cover for Track Assembly */}
              {needsTrackOptions && (
              <div className="border-t pt-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Track Options</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      U-Channel
                    </label>
                    <select
                      value={options.uChannel}
                      onChange={e => setOptions({ ...options, uChannel: e.target.value as 'none' | 'left' | 'right' | 'both' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">None</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="both">Both</option>
                    </select>
                    {/* Matches "Apply Factory Deductions" on the order form. Selecting a
                        u-channel does NOT deduct on its own — this is the switch. */}
                    <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.applyUChannelDeduction}
                        onChange={e => setOptions({ ...options, applyUChannelDeduction: e.target.checked })}
                        disabled={options.uChannel === 'none'}
                        className="rounded border-gray-300"
                      />
                      Apply factory deductions (&minus;0.375&quot; per side)
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Screw Covers
                    </label>
                    <select
                      value={options.screwCover}
                      onChange={e => setOptions({ ...options, screwCover: e.target.value as 'none' | 'left' | 'right' | 'both' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">None</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="both">Both</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Used when no U-Channel on that side</p>
                  </div>
                </div>
              </div>
              )}

              {/* Configuration Options Section - only show for full screen or when no assembly selected */}
              {needsFullOptions && (
              <div className="border-t pt-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Configuration Options</h3>

                {/* Product Line & Version Selection */}
                {productLines.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">
                        Product Line
                      </label>
                      <select
                        value={options.productLineId}
                        onChange={e => {
                          const plId = e.target.value;
                          setOptions({ ...options, productLineId: plId, productLineVersionId: '' });
                        }}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white"
                      >
                        <option value="">All Product Lines</option>
                        {productLines.filter(pl => pl.isActive).map(pl => (
                          <option key={pl.id} value={pl.id}>{pl.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-purple-800 mb-1">
                        Version
                      </label>
                      <select
                        value={options.productLineVersionId}
                        onChange={e => setOptions({ ...options, productLineVersionId: e.target.value })}
                        disabled={!options.productLineId}
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white disabled:opacity-50 disabled:bg-gray-100"
                      >
                        <option value="">Latest Active Version</option>
                        {options.productLineId && productLines
                          .find(pl => pl.id === options.productLineId)
                          ?.versions
                          ?.filter(v => v.status === 'active' || v.status === 'warranty_only')
                          .map(v => (
                            <option key={v.id} value={v.id}>
                              V{v.versionNumber} {v.versionName ? `- ${v.versionName}` : ''} ({v.status === 'active' ? 'Active' : 'Warranty Only'})
                            </option>
                          ))
                        }
                      </select>
                      <p className="text-xs text-purple-600 mt-1">Select version for locked BOM</p>
                    </div>
                    <div className="flex items-end">
                      {options.productLineVersionId && (
                        <span className="px-3 py-2 bg-purple-100 text-purple-800 rounded-lg text-sm">
                          Using locked BOM snapshot
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      System Type
                    </label>
                    <select
                      value={options.systemType}
                      onChange={e => setOptions({ ...options, systemType: e.target.value as 'v_track' | 'open_roll' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="v_track">Housing Box</option>
                      <option value="open_roll">Open Roll</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      U-Channel
                    </label>
                    <select
                      value={options.uChannel}
                      onChange={e => setOptions({ ...options, uChannel: e.target.value as 'none' | 'left' | 'right' | 'both' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">None</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="both">Both</option>
                    </select>
                    {/* Matches "Apply Factory Deductions" on the order form. Selecting a
                        u-channel does NOT deduct on its own — this is the switch. */}
                    <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={options.applyUChannelDeduction}
                        onChange={e => setOptions({ ...options, applyUChannelDeduction: e.target.checked })}
                        disabled={options.uChannel === 'none'}
                        className="rounded border-gray-300"
                      />
                      Apply factory deductions (&minus;0.375&quot; per side)
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Screw Covers
                    </label>
                    <select
                      value={options.screwCover}
                      onChange={e => setOptions({ ...options, screwCover: e.target.value as 'none' | 'left' | 'right' | 'both' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">None</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Brush Size
                    </label>
                    <select
                      value={options.brushSize}
                      onChange={e => setOptions({ ...options, brushSize: e.target.value as '1' | '2' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="1">1 inch</option>
                      <option value="2">2 inch</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shipping Method
                    </label>
                    <select
                      value={options.shippingMethod}
                      onChange={e => setOptions({ ...options, shippingMethod: e.target.value as ShippingMethod })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="will_call">Will Call</option>
                      <option value="freight">Freight Shipping</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shipping Cost ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={options.shippingCost || ''}
                      onChange={e => setOptions({ ...options, shippingCost: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Freight Quote Section */}
                {options.shippingMethod === 'freight' && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-800 mb-3">Get Freight Quote</h4>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Dest. ZIP</label>
                        <input
                          type="text"
                          maxLength={5}
                          value={bomFreightZip}
                          onChange={e => setBomFreightZip(e.target.value.replace(/\D/g, ''))}
                          placeholder="ZIP"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Length"</label>
                        <input
                          type="number"
                          value={bomFreightDimensions.length}
                          onChange={e => setBomFreightDimensions({ ...bomFreightDimensions, length: parseInt(e.target.value) || 0 })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Width"</label>
                        <input
                          type="number"
                          value={bomFreightDimensions.width}
                          onChange={e => setBomFreightDimensions({ ...bomFreightDimensions, width: parseInt(e.target.value) || 0 })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Height"</label>
                        <input
                          type="number"
                          value={bomFreightDimensions.height}
                          onChange={e => setBomFreightDimensions({ ...bomFreightDimensions, height: parseInt(e.target.value) || 0 })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Weight lbs</label>
                        <input
                          type="number"
                          value={bomFreightDimensions.weight}
                          onChange={e => setBomFreightDimensions({ ...bomFreightDimensions, weight: parseInt(e.target.value) || 0 })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Pallets</label>
                        <input
                          type="number"
                          min="1"
                          value={bomFreightDimensions.pallets}
                          onChange={e => setBomFreightDimensions({ ...bomFreightDimensions, pallets: parseInt(e.target.value) || 1 })}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 mb-3">
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={bomFreightAccessorials.liftgate}
                          onChange={e => setBomFreightAccessorials({ ...bomFreightAccessorials, liftgate: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Liftgate
                      </label>
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={bomFreightAccessorials.residential}
                          onChange={e => setBomFreightAccessorials({ ...bomFreightAccessorials, residential: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Residential
                      </label>
                      <label className="flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          checked={bomFreightAccessorials.inside}
                          onChange={e => setBomFreightAccessorials({ ...bomFreightAccessorials, inside: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Inside Delivery
                      </label>
                      <button
                        onClick={fetchBomFreightQuotes}
                        disabled={bomFreightLoading || !bomFreightZip}
                        className="ml-auto px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {bomFreightLoading ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Getting Quotes...
                          </>
                        ) : 'Get Quotes'}
                      </button>
                    </div>
                    {bomFreightError && (
                      <div className="p-2 bg-red-100 text-red-700 text-sm rounded mb-2">{bomFreightError}</div>
                    )}
                    {bomFreightQuotes.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {bomFreightQuotes.map((quote, idx) => (
                          <button
                            key={quote.quoteId || idx}
                            onClick={() => setOptions({ ...options, shippingCost: quote.totalAmount })}
                            className={`w-full p-2 text-left border rounded flex justify-between items-center transition-colors ${
                              options.shippingCost === quote.totalAmount
                                ? 'border-blue-500 bg-blue-100'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <div>
                              <span className="font-medium text-sm">{quote.carrierName}</span>
                              <span className="text-xs text-gray-500 ml-2">{quote.serviceDescription || 'LTL'}</span>
                              {quote.transitTime && <span className="text-xs text-gray-400 ml-2">({quote.transitTime})</span>}
                            </div>
                            <span className="font-bold text-green-700">${quote.totalAmount?.toFixed(2)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Window Panel option - show for Full Screen and Fabric Panel */}
                {needsWindowOptions && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <label className="flex items-center gap-2 cursor-pointer pt-2">
                          <input
                            type="checkbox"
                            checked={options.windowPanel}
                            onChange={e => setOptions({ ...options, windowPanel: e.target.checked, windowCount: e.target.checked ? 1 : 0 })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">Window Panel</span>
                        </label>
                      </div>
                    </div>

                    {/* Number of Windows - only show when Window Panel is checked */}
                    {options.windowPanel && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Number of Windows
                          </label>
                          <input
                            type="number"
                            value={options.windowCount}
                            onChange={e => setOptions({ ...options, windowCount: parseInt(e.target.value) || 1 })}
                            onWheel={(e) => e.currentTarget.blur()}
                            min="1"
                            max="20"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* U-Channel Info Box */}
                {uChannelQty > 0 && (
                  <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>U-Channel:</strong> {uChannelQty} x H_system ({varsDisplay.H_system}")
                    </p>
                  </div>
                )}
              </div>
              )}

              {/* Frame/Material Section - show fabric options for fabric assemblies or full screen */}
              {needsFabric && (
              <div className="border-t pt-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Fabric Selection</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {needsFullOptions && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frame Color
                    </label>
                    <select
                      value={options.frameColor}
                      onChange={e => setOptions({ ...options, frameColor: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="black">Black</option>
                      <option value="white">White</option>
                      <option value="bronze">Bronze</option>
                      <option value="tan">Tan</option>
                      <option value="mill">Mill Finish (+$495 Powder Coat)</option>
                    </select>
                    {options.frameColor === 'mill' && (
                      <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-xs text-purple-700">Mill finish parts require powder coating: <strong>+$495.00</strong> per screen</p>
                      </div>
                    )}
                  </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fabric Type
                    </label>
                    <select
                      value={options.fabricType}
                      onChange={e => {
                        const newFabricType = e.target.value;
                        const newColors = fabricColorMap[newFabricType] || [];
                        const defaultColor = newColors.length > 0 ? newColors[0].value : 'black';
                        setOptions({ ...options, fabricType: newFabricType, fabricColor: defaultColor });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {(() => {
                        const groups = new Map<string, typeof fabricTypes>();
                        for (const ft of fabricTypes) {
                          if (!groups.has(ft.group)) groups.set(ft.group, []);
                          groups.get(ft.group)!.push(ft);
                        }
                        return Array.from(groups.entries()).map(([group, types]) => (
                          <optgroup key={group} label={group}>
                            {types.map(ft => (
                              <option key={ft.value} value={ft.value}>{ft.label}</option>
                            ))}
                          </optgroup>
                        ));
                      })()}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fabric Color
                    </label>
                    <select
                      value={options.fabricColor}
                      onChange={e => setOptions({ ...options, fabricColor: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {(fabricColorMap[options.fabricType] || []).map(color => (
                        <option key={color.value} value={color.value}>{color.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              )}

              {/* Controls Section - show motor options for roller assemblies or full screen */}
              {needsMotor && (
              <div className="border-t pt-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Controls</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motor Type
                    </label>
                    <select
                      value={options.motorType}
                      onChange={e => {
                        const newMotorType = e.target.value as 'none' | 'keder' | 'alpha' | 'somfy';
                        const remoteOptions = REMOTE_OPTIONS[newMotorType] || [];
                        const defaultRemote = remoteOptions.length > 0 ? remoteOptions[0].value : '';
                        const wallSwitchOptions = WALL_SWITCH_OPTIONS[newMotorType] || [];
                        const defaultWallSwitch = wallSwitchOptions.length > 0 ? wallSwitchOptions[0].value : 'none';
                        setOptions({
                          ...options,
                          motorType: newMotorType,
                          remoteType: defaultRemote,
                          remoteQty: newMotorType === 'none' ? 0 : 1,
                          wallSwitchType: defaultWallSwitch,
                          wallSwitchQty: 0,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="none">No Motor</option>
                      <option value="keder">Keder Motor</option>
                      <option value="alpha">Alpha Motor</option>
                      <option value="somfy">Somfy Motor</option>
                    </select>
                  </div>
                  {options.motorType !== 'none' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Remote Type
                        </label>
                        <select
                          value={options.remoteType}
                          onChange={e => setOptions({ ...options, remoteType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {(REMOTE_OPTIONS[options.motorType] || []).map(remote => (
                            <option key={remote.value} value={remote.value}>{remote.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Remote QTY
                        </label>
                        <input
                          type="number"
                          value={options.remoteQty}
                          onChange={e => setOptions({ ...options, remoteQty: parseInt(e.target.value) || 0 })}
                          onWheel={(e) => e.currentTarget.blur()}
                          min="0"
                          max="10"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </>
                  )}
                </div>
                {options.motorType !== 'none' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Wall Switch
                      </label>
                      <select
                        value={options.wallSwitchType}
                        onChange={e => setOptions({
                          ...options,
                          wallSwitchType: e.target.value,
                          wallSwitchQty: e.target.value === 'none' ? 0 : (options.wallSwitchQty || 1),
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {(WALL_SWITCH_OPTIONS[options.motorType] || []).map(ws => (
                          <option key={ws.value} value={ws.value}>{ws.label}</option>
                        ))}
                      </select>
                    </div>
                    {options.wallSwitchType !== 'none' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Wall Switch QTY
                        </label>
                        <input
                          type="number"
                          value={options.wallSwitchQty}
                          onChange={e => setOptions({ ...options, wallSwitchQty: parseInt(e.target.value) || 0 })}
                          onWheel={(e) => e.currentTarget.blur()}
                          min="1"
                          max="10"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Shipping Section - always visible for all assemblies */}
              <div className="border-t pt-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Shipping</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shipping Method
                    </label>
                    <select
                      value={options.shippingMethod}
                      onChange={e => setOptions({ ...options, shippingMethod: e.target.value as ShippingMethod })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="will_call">Will Call</option>
                      <option value="freight">Freight Shipping</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shipping Cost ($)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={options.shippingCost || ''}
                      onChange={e => setOptions({ ...options, shippingCost: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Calculated Variables */}
              {(parseFloat(width) > 0 || parseFloat(height) > 0) && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">
                    <strong>Calculated Variables:</strong>
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-600 font-mono">
                    <span>W_system = {varsDisplay.W_system}"</span>
                    <span>H_system = {varsDisplay.H_system}"</span>
                    <span>holes = {varsDisplay.holes}</span>
                    <span>seam_count = {varsDisplay.seam_count}</span>
                    <span>u_channel = {options.uChannel}</span>
                    <span>u_channel_qty = {uChannelQty}</span>
                    <span>screw_cover = {options.screwCover}</span>
                    <span>screw_cover_qty = {screwCoverQty}</span>
                    <span>brush_size = {options.brushSize}"</span>
                    <span>shipping_method = {options.shippingMethod}</span>
                    <span>motor_type = {options.motorType}</span>
                    <span>window_panel = {options.windowPanel ? 'true' : 'false'}</span>
                    {options.windowPanel && <span>window_count = {options.windowCount}</span>}
                    <span>frame_color = {options.frameColor}</span>
                    <span>fabric_type = {options.fabricType}</span>
                  </div>
                </div>
              )}
                  </>
                );
              })()}
            </Card>

          {/* Custom Build Section */}
          {selectedAssembly?.isCustom && (
            <Card>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Custom Build</h2>
                <button
                  onClick={() => setShowItemPicker(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <span>+</span> Add Items
                </button>
              </div>

              {customBomItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <p className="text-lg">No items added yet</p>
                  <p className="text-sm mt-2">Click "Add Items" to select from inventory</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="pb-2 font-medium">Part #</th>
                          <th className="pb-2 font-medium">Description</th>
                          <th className="pb-2 font-medium">Group</th>
                          <th className="pb-2 font-medium text-center" style={{ width: '80px' }}>Qty</th>
                          <th className="pb-2 font-medium text-right" style={{ width: '80px' }}>Cut Size</th>
                          <th className="pb-2 font-medium text-right">Unit Cost</th>
                          <th className="pb-2 font-medium text-right">Total</th>
                          <th className="pb-2 font-medium text-center" style={{ width: '50px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {customBomItems.map(item => (
                          <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2 font-mono text-gray-900">{item.partNumber}</td>
                            <td className="py-2 text-gray-700 max-w-xs truncate" title={item.description}>
                              {item.description}
                            </td>
                            <td className="py-2 text-gray-500 text-xs">{item.inventoryGroup || '-'}</td>
                            <td className="py-2 text-center">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={e => updateCustomBomQuantity(item.id, parseFloat(e.target.value) || 0)}
                                onWheel={(e) => e.currentTarget.blur()}
                                min="0"
                                step="0.01"
                                className="w-16 px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                              />
                            </td>
                            <td className="py-2 text-right text-gray-600 font-mono">
                              {item.cutSize ? `${item.cutSize}"` : '-'}
                            </td>
                            <td className="py-2 text-right text-gray-600">
                              {item.stockTakeType === 'linear_from_roll' && item.cutSize ? (
                                <span title="Price per inch">
                                  {formatCurrency(item.unitCost)}/in
                                </span>
                              ) : item.cutSize && item.stockLength && item.stockLength > 0 ? (
                                <span title={`Full: ${formatCurrency(item.unitCost)} for ${item.stockLength}"`}>
                                  {formatCurrency(item.unitCost)}/{item.stockLength}"
                                </span>
                              ) : (
                                formatCurrency(item.unitCost)
                              )}
                            </td>
                            <td className="py-2 text-right font-medium">{formatCurrency(calculateItemCost(item))}</td>
                            <td className="py-2 text-center">
                              <button
                                onClick={() => removeFromCustomBom(item.id)}
                                className="text-red-500 hover:text-red-700"
                                title="Remove item"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-purple-50 font-semibold">
                          <td colSpan={6} className="py-3 text-right pr-4">Custom BOM Total:</td>
                          <td className="py-3 text-right text-lg">{formatCurrency(customBomTotal)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Margin Calculator for Custom BOM */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-md font-semibold text-gray-900 mb-4">Margin Calculator</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      For custom builds, enter MSRP manually or enter dimensions above to auto-calculate.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          MSRP Price
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={msrpOverride}
                            onChange={e => setMsrpOverride(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dealer Discount %
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={dealerDiscount}
                            onChange={e => setDealerDiscount(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="40"
                            min="0"
                            max="100"
                            className="w-full pr-8 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                          <span className="absolute right-3 top-2 text-gray-500">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dealer Price
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg font-medium">
                          {formatCurrency(marginCalc.dealerPrice)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          BOM Cost
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg font-medium">
                          {formatCurrency(customBomTotal)}
                        </div>
                      </div>
                    </div>

                    {marginCalc.dealerPrice > 0 && (
                      <div className={clsx(
                        'mt-4 p-4 rounded-lg',
                        marginCalc.marginPercent >= 50 ? 'bg-green-50 border border-green-200' :
                        marginCalc.marginPercent >= 30 ? 'bg-yellow-50 border border-yellow-200' :
                        'bg-red-50 border border-red-200'
                      )}>
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">Gross Margin:</span>
                          <span className={clsx(
                            'text-xl font-bold',
                            marginCalc.marginPercent >= 50 ? 'text-green-700' :
                            marginCalc.marginPercent >= 30 ? 'text-yellow-700' :
                            'text-red-700'
                          )}>
                            {formatCurrency(marginCalc.grossMargin)} ({marginCalc.marginPercent.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Create Special Order Button for Custom BOM */}
                  {customBomTotal > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <button
                        onClick={() => setShowSpecialOrderModal(true)}
                        className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 font-medium"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Create Special Order from Custom BOM
                      </button>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Create an invoice for this custom build as a standalone order
                      </p>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          {/* BOM Results for Standard Assemblies */}
          {selectedAssembly && !selectedAssembly.isCustom && (
            <Card>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  BOM: {selectedAssembly.name}
                  {isEditingBom && <span className="ml-2 text-sm text-orange-600">(Editing)</span>}
                </h2>
                <div className="flex items-center gap-2">
                  {bomLines.length > 0 && (
                    <>
                      <span className="text-sm text-gray-500 mr-2">
                        {bomLines.length} line items
                      </span>
                      {/* Add to Build button - for combining multiple assemblies */}
                      {!selectedAssembly?.isFullScreen && !isEditingBom && (
                        <button
                          onClick={addAssemblyToBuild}
                          className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1"
                          title="Add this assembly to a multi-assembly build"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add to Build
                        </button>
                      )}
                      {!isEditingBom ? (
                        <button
                          onClick={() => setIsEditingBom(true)}
                          className="px-3 py-1.5 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Edit BOM
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => setShowBomItemPicker(true)}
                            className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center gap-1"
                          >
                            <span>+</span> Add Parts
                          </button>
                          {loadedSavedBomId && (
                            <button
                              onClick={updateLoadedBom}
                              disabled={savingCustomBom}
                              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1 disabled:opacity-50"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                              {savingCustomBom ? 'Updating...' : 'Update BOM'}
                            </button>
                          )}
                          <button
                            onClick={() => setShowSaveModal(true)}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                            </svg>
                            Save as New
                          </button>
                          <button
                            onClick={resetBomEdits}
                            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>

              {serverBomError ? (
                <div className="text-center py-8">
                  <p className="text-red-600 font-medium">Could not cost this BOM</p>
                  <p className="text-sm text-gray-600 mt-1">{serverBomError}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Full Screen Unit is costed by the server so it matches the released
                    cut sheet. Rather than show different numbers from a second
                    calculation, nothing is shown until this succeeds.
                  </p>
                </div>
              ) : serverBomLoading && bomLines.length === 0 ? (
                <div className="text-center py-8 text-gray-500"><p>Costing BOM…</p></div>
              ) : bomLines.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {!width || !height ? (
                    <p>Enter width and height to calculate BOM</p>
                  ) : (
                    <p>No items with BOM rules found for this assembly</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          {isEditingBom && <th className="pb-2 font-medium w-10"></th>}
                          <th className="pb-2 font-medium">Part #</th>
                          <th className="pb-2 font-medium">Description</th>
                          <th className="pb-2 font-medium text-right">Cut Length</th>
                          <th className="pb-2 font-medium text-right">Qty</th>
                          <th className="pb-2 font-medium text-right">Unit Cost</th>
                          <th className="pb-2 font-medium text-right">Prorated</th>
                          <th className="pb-2 font-medium text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bomLines.map(line => {
                          const edit = editedBomItems.get(line.id);
                          const isRemoved = edit?.removed || false;
                          const qty = edit?.quantityOverride ?? line.quantity;
                          const effectiveCost = line.proratedCost ?? line.unitCost;

                          return (
                            <tr key={line.id} className={clsx(
                              'border-b border-gray-100',
                              isRemoved && 'bg-red-50 opacity-50',
                              !isRemoved && line.isExtrusion && 'bg-blue-50/30',
                              !isRemoved && !line.isExtrusion && line.hasCutFormula && 'bg-purple-50/30',
                              !isRemoved && 'hover:bg-gray-50'
                            )}>
                              {isEditingBom && (
                                <td className="py-2">
                                  <button
                                    onClick={() => toggleBomItemRemoved(line.id)}
                                    className={clsx(
                                      'w-6 h-6 rounded flex items-center justify-center text-xs',
                                      isRemoved
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                                    )}
                                    title={isRemoved ? 'Restore item' : 'Remove item'}
                                  >
                                    {isRemoved ? '↩' : '✕'}
                                  </button>
                                </td>
                              )}
                              <td className={clsx('py-2 font-mono text-gray-900', isRemoved && 'line-through')}>
                                {line.partNumber}
                              </td>
                              <td className={clsx('py-2 text-gray-700 max-w-xs truncate', isRemoved && 'line-through')} title={line.description}>
                                {line.description}
                                {line.isExtrusion && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                    Extrusion
                                  </span>
                                )}
                                {!line.isExtrusion && line.hasCutFormula && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                    Cut Formula
                                  </span>
                                )}
                              </td>
                              <td className="py-2 text-right font-mono text-gray-600">
                                {line.cutLength ? (
                                  <span title={line.stockLength ? `Stock: ${line.stockLength}"` : 'From formula'}>
                                    {line.cutLength.toFixed(2)}"
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="py-2 text-right">
                                {isEditingBom && !isRemoved ? (
                                  <input
                                    type="number"
                                    value={qty}
                                    onChange={e => updateBomItemQuantity(line.id, parseFloat(e.target.value) || 0)}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    min="0"
                                    step="0.01"
                                    className="w-16 px-1 py-0.5 text-right border border-gray-300 rounded text-sm"
                                  />
                                ) : (
                                  <span className="font-medium">{qty.toFixed(2)}</span>
                                )}
                              </td>
                              <td className="py-2 text-right text-gray-600">
                                {line.stockLength ? (
                                  <span className="text-xs" title={`Per ${line.stockLength}" stick`}>
                                    {formatCurrency(line.unitCost)}
                                  </span>
                                ) : formatCurrency(line.unitCost)}
                              </td>
                              <td className="py-2 text-right text-gray-600">
                                {line.proratedCost !== undefined ? (
                                  <span className="text-green-600 font-medium" title={`${((line.cutLength || 0) / (line.stockLength || 1) * 100).toFixed(1)}% of stock`}>
                                    {formatCurrency(line.proratedCost)}
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="py-2 text-right font-medium">
                                {isRemoved ? '—' : formatCurrency(qty * effectiveCost)}
                              </td>
                            </tr>
                          );
                        })}
                        {/* Added items during editing */}
                        {isEditingBom && addedBomItems.map(item => (
                          <tr key={`added-${item.id}`} className="border-b border-gray-100 bg-green-50">
                            <td className="py-2">
                              <button
                                onClick={() => removeAddedBomItem(item.id)}
                                className="w-6 h-6 rounded flex items-center justify-center text-xs bg-red-100 text-red-700 hover:bg-red-200"
                                title="Remove added item"
                              >
                                ✕
                              </button>
                            </td>
                            <td className="py-2 font-mono text-gray-900">
                              {item.partNumber}
                              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                Added
                              </span>
                            </td>
                            <td className="py-2 text-gray-700 max-w-xs truncate" title={item.description}>
                              {item.description}
                            </td>
                            <td className="py-2 text-right font-mono text-gray-600">—</td>
                            <td className="py-2 text-right">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={e => updateAddedBomItemQuantity(item.id, parseFloat(e.target.value) || 0)}
                                onWheel={(e) => e.currentTarget.blur()}
                                min="0"
                                step="0.01"
                                className="w-16 px-1 py-0.5 text-right border border-gray-300 rounded text-sm"
                              />
                            </td>
                            <td className="py-2 text-right text-gray-600">{formatCurrency(item.unitCost)}</td>
                            <td className="py-2 text-right text-gray-600">—</td>
                            <td className="py-2 text-right font-medium">{formatCurrency(item.quantity * item.unitCost)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        {/* Powder Coating Charge Row */}
                        {options.frameColor === 'mill' && (
                          <tr className="bg-purple-50 border-t border-purple-200">
                            <td className="py-2"></td>
                            <td className="py-2 font-mono text-purple-900">POWDER-COAT</td>
                            <td className="py-2 text-purple-700">Powder Coating (Mill Finish)</td>
                            <td className="py-2 text-right font-mono text-purple-600">—</td>
                            <td className="py-2 text-right text-purple-700">1</td>
                            <td className="py-2 text-right text-purple-600">{formatCurrency(powderCoatCharge)}</td>
                            <td className="py-2 text-right text-purple-600">—</td>
                            <td className="py-2 text-right font-medium text-purple-800">{formatCurrency(powderCoatCharge)}</td>
                          </tr>
                        )}
                        <tr className={clsx('font-semibold', isEditingBom ? 'bg-orange-50' : 'bg-gray-50')}>
                          <td colSpan={isEditingBom ? 7 : 6} className="py-3 text-right pr-4">
                            {isEditingBom ? 'Edited BOM Cost:' : 'BOM Cost:'}
                          </td>
                          <td className="py-3 text-right text-lg">
                            {formatCurrency(isEditingBom ? editedBomTotal : totalBomCost)}
                          </td>
                        </tr>
                        {options.shippingCost > 0 && (
                          <>
                            <tr className="bg-blue-50">
                              <td colSpan={isEditingBom ? 7 : 6} className="py-2 text-right pr-4 text-blue-700">
                                + Shipping:
                              </td>
                              <td className="py-2 text-right text-blue-700">
                                {formatCurrency(options.shippingCost)}
                              </td>
                            </tr>
                            <tr className="font-bold bg-green-50">
                              <td colSpan={isEditingBom ? 7 : 6} className="py-3 text-right pr-4 text-green-800">
                                Grand Total:
                              </td>
                              <td className="py-3 text-right text-lg text-green-700">
                                {formatCurrency((isEditingBom ? editedBomTotal : totalBomCost) + options.shippingCost)}
                              </td>
                            </tr>
                          </>
                        )}
                      </tfoot>
                    </table>
                  </div>

                  {/* Create Special Order Button */}
                  {totalBomCost > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <button
                        onClick={() => setShowSpecialOrderModal(true)}
                        className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 font-medium"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Create Special Order from BOM
                      </button>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Create an invoice for this assembly as a standalone order
                      </p>
                    </div>
                  )}

                  {/* Margin Calculator */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-md font-semibold text-gray-900 mb-4">Margin Calculator</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          MSRP Price {marginCalc.isCalculated && <span className="text-green-600 text-xs">(auto)</span>}
                        </label>
                        {calculatedMsrp ? (
                          <div className="relative">
                            <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg font-medium text-green-800">
                              {formatCurrency(marginCalc.msrp)}
                            </div>
                            <button
                              onClick={() => setMsrpOverride(calculatedMsrp.toString())}
                              className="absolute right-2 top-2 text-xs text-blue-600 hover:underline"
                              title="Override calculated MSRP"
                            >
                              edit
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={msrpOverride}
                              onChange={e => setMsrpOverride(e.target.value)}
                              onWheel={(e) => e.currentTarget.blur()}
                              placeholder="Enter dimensions"
                              min="0"
                              step="0.01"
                              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        )}
                        {msrpOverride && calculatedMsrp && (
                          <button
                            onClick={() => setMsrpOverride('')}
                            className="text-xs text-gray-500 hover:text-blue-600 mt-1"
                          >
                            Reset to calculated ({formatCurrency(calculatedMsrp)})
                          </button>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dealer Discount %
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={dealerDiscount}
                            onChange={e => setDealerDiscount(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                            placeholder="40"
                            min="0"
                            max="100"
                            className="w-full pr-8 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="absolute right-3 top-2 text-gray-500">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dealer Price
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg font-medium">
                          {formatCurrency(marginCalc.dealerPrice)}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          BOM Cost
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg font-medium">
                          {formatCurrency(totalBomCost)}
                        </div>
                      </div>
                    </div>

                    {marginCalc.dealerPrice > 0 && (
                      <div className={clsx(
                        'mt-4 p-4 rounded-lg',
                        marginCalc.marginPercent >= 50 ? 'bg-green-50 border border-green-200' :
                        marginCalc.marginPercent >= 30 ? 'bg-yellow-50 border border-yellow-200' :
                        'bg-red-50 border border-red-200'
                      )}>
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">Gross Margin:</span>
                          <span className={clsx(
                            'text-xl font-bold',
                            marginCalc.marginPercent >= 50 ? 'text-green-700' :
                            marginCalc.marginPercent >= 30 ? 'text-yellow-700' :
                            'text-red-700'
                          )}>
                            {formatCurrency(marginCalc.grossMargin)} ({marginCalc.marginPercent.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card>
          )}

          {!selectedAssembly && (
            <Card>
              <div className="text-center py-12 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <p className="text-lg">Select an assembly to calculate BOM</p>
                <p className="text-sm mt-2">Choose from the assembly list on the left, or use Custom Build to select individual items</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Item Picker Modal */}
      {showItemPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Add Items to Custom BOM</h3>
              <button
                onClick={() => setShowItemPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Filters */}
            <div className="p-4 border-b border-gray-200 flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by part # or description..."
                  value={itemSearch}
                  onChange={e => setItemSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  autoFocus
                />
              </div>
              <div className="w-48">
                <select
                  value={selectedGroup}
                  onChange={e => setSelectedGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Groups</option>
                  {groups.filter(g => g.isActive).map(g => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Item List */}
            <div className="flex-1 overflow-y-auto p-4">
              {pickerItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No items found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pickerItems.map(item => {
                    const i = item as any;
                    const partNumber = i.partNumber || i.part_number || '';
                    const unitCost = (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0);
                    const group = i.inventoryGroup || i.inventory_group || '-';
                    const isAdded = customBomItems.some(c => c.id === item.id);

                    return (
                      <div
                        key={item.id}
                        className={clsx(
                          'p-3 rounded-lg border flex justify-between items-center',
                          isAdded
                            ? 'border-purple-300 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-gray-900">{partNumber}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{group}</span>
                          </div>
                          <p className="text-sm text-gray-600 truncate">{item.description}</p>
                        </div>
                        <div className="flex items-center gap-4 ml-4">
                          <span className="text-sm text-gray-600">{formatCurrency(unitCost)}</span>
                          {isAdded ? (
                            <span className="text-purple-600 font-medium text-sm">Added ✓</span>
                          ) : (
                            <button
                              onClick={() => addToCustomBom(item)}
                              className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700"
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {pickerItems.length >= 50 && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  Showing first 50 results. Use search to find more items.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {customBomItems.length} items selected ({formatCurrency(customBomTotal)} total)
              </span>
              <button
                onClick={() => setShowItemPicker(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BOM Edit Item Picker Modal */}
      {showBomItemPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Add Parts to BOM</h3>
              <button
                onClick={() => setShowBomItemPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Filters */}
            <div className="p-4 border-b border-gray-200 flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by part # or description..."
                  value={itemSearch}
                  onChange={e => setItemSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  autoFocus
                />
              </div>
              <div className="w-48">
                <select
                  value={selectedGroup}
                  onChange={e => setSelectedGroup(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                >
                  <option value="">All Groups</option>
                  {groups.filter(g => g.isActive).map(g => (
                    <option key={g.id} value={g.name}>{g.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Item List */}
            <div className="flex-1 overflow-y-auto p-4">
              {pickerItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No items found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {pickerItems.map(item => {
                    const i = item as any;
                    const partNumber = i.partNumber || i.part_number || '';
                    const unitCost = (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0);
                    const group = i.inventoryGroup || i.inventory_group || '-';
                    const isInBom = bomLines.some(b => b.id === item.id);
                    const isAdded = addedBomItems.some(a => a.id === item.id);

                    return (
                      <div
                        key={item.id}
                        className={clsx(
                          'p-3 rounded-lg border flex justify-between items-center',
                          isInBom || isAdded
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-gray-900">{partNumber}</span>
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{group}</span>
                          </div>
                          <p className="text-sm text-gray-600 truncate">{item.description}</p>
                        </div>
                        <div className="flex items-center gap-4 ml-4">
                          <span className="text-sm text-gray-600">{formatCurrency(unitCost)}</span>
                          {isInBom ? (
                            <span className="text-blue-600 font-medium text-sm">In BOM</span>
                          ) : isAdded ? (
                            <span className="text-green-600 font-medium text-sm">Added ✓</span>
                          ) : (
                            <button
                              onClick={() => addItemToBomEdit(item)}
                              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {pickerItems.length >= 50 && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  Showing first 50 results. Use search to find more items.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {addedBomItems.length} items added
              </span>
              <button
                onClick={() => setShowBomItemPicker(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save as Custom BOM Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Save as Custom BOM</h3>
              <button
                onClick={() => setShowSaveModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Custom BOM Name *
                </label>
                <input
                  type="text"
                  value={customBomName}
                  onChange={e => setCustomBomName(e.target.value)}
                  placeholder="e.g., 120x120 Face Mount Solar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">BOM Summary</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Base Assembly:</strong> {selectedAssembly?.name}</p>
                  <p><strong>Dimensions:</strong> {width}" × {height}"</p>
                  <p><strong>Items:</strong> {bomLines.filter(l => !editedBomItems.get(l.id)?.removed).length + addedBomItems.length} parts</p>
                  <p><strong>Total Cost:</strong> {formatCurrency(editedBomTotal)}</p>
                  {editedBomItems.size > 0 && (
                    <p className="text-orange-600">
                      <strong>Modifications:</strong> {Array.from(editedBomItems.values()).filter(e => e.removed).length} removed,{' '}
                      {Array.from(editedBomItems.values()).filter(e => e.quantityOverride !== undefined).length} qty changed
                    </p>
                  )}
                  {addedBomItems.length > 0 && (
                    <p className="text-green-600">
                      <strong>Added Parts:</strong> {addedBomItems.length}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={saveAsCustomBom}
                disabled={!customBomName.trim() || savingCustomBom}
                className={clsx(
                  'px-4 py-2 rounded-lg flex items-center gap-2',
                  customBomName.trim() && !savingCustomBom
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                )}
              >
                {savingCustomBom ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    Save Custom BOM
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Assembly Modal */}
      {showEditAssemblyModal && editingAssembly && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Assembly: {editingAssembly.name}</h3>
                <p className="text-sm text-gray-500">Add or remove parts from this assembly</p>
              </div>
              <button
                onClick={() => setShowEditAssemblyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Content - Two columns */}
            <div className="flex-1 overflow-hidden flex">
              {/* Left: Current items in assembly */}
              <div className="w-1/2 border-r border-gray-200 flex flex-col">
                <div className="p-3 border-b border-gray-200 bg-gray-50">
                  <h4 className="font-medium text-gray-900">
                    Items in Assembly ({editingAssemblyItems.length})
                  </h4>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {editingAssemblyItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No items in this assembly</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {editingAssemblyItems.map(item => {
                        const i = item as any;
                        const partNumber = i.partNumber || i.part_number || '';
                        const unitCost = (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0);

                        return (
                          <div
                            key={item.id}
                            className="p-3 rounded-lg border border-gray-200 bg-white hover:border-gray-300"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <span className="font-mono text-sm font-medium text-gray-900">{partNumber}</span>
                                <p className="text-sm text-gray-600 truncate">{item.description}</p>
                                <span className="text-xs text-gray-500">{formatCurrency(unitCost)}</span>
                              </div>
                              <button
                                onClick={() => removeItemFromAssembly(item.id)}
                                disabled={savingAssemblyChanges}
                                className="ml-2 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
                                title="Remove from assembly"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Available items to add */}
              <div className="w-1/2 flex flex-col">
                <div className="p-3 border-b border-gray-200 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-2">Add Items</h4>
                  <input
                    type="text"
                    placeholder="Search by part # or description..."
                    value={assemblyItemSearch}
                    onChange={e => setAssemblyItemSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {availableItemsForAssembly.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>{assemblyItemSearch ? 'No items match search' : 'Start typing to search'}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {availableItemsForAssembly.map(item => {
                        const i = item as any;
                        const partNumber = i.partNumber || i.part_number || '';
                        const unitCost = (parseFloat(i.landedCost || i.landed_cost || '0') > 0
        ? parseFloat(i.landedCost || i.landed_cost)
        : parseFloat(i.unitCost || i.unit_cost || i.unit_price || '0') || 0);
                        const currentGroup = i.inventoryGroup || i.inventory_group || '';

                        return (
                          <div
                            key={item.id}
                            className="p-3 rounded-lg border border-gray-200 bg-white hover:border-blue-300"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-sm font-medium text-gray-900">{partNumber}</span>
                                  {currentGroup && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{currentGroup}</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 truncate">{item.description}</p>
                                <span className="text-xs text-gray-500">{formatCurrency(unitCost)}</span>
                              </div>
                              <button
                                onClick={() => addItemToAssembly(item.id)}
                                disabled={savingAssemblyChanges}
                                className="ml-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {availableItemsForAssembly.length >= 50 && (
                    <p className="text-center text-sm text-gray-500 mt-4">
                      Showing first 50 results. Use search to find more.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {editingAssemblyItems.length} items in {editingAssembly.name}
              </span>
              <button
                onClick={() => setShowEditAssemblyModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Saved BOMs Management Modal */}
      {showSavedBomsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Saved BOMs</h3>
              <button
                onClick={() => setShowSavedBomsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingSavedBoms ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : savedBoms.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>No saved BOMs yet</p>
                  <p className="text-sm mt-1">Use "Save as Custom BOM" to save your configurations</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedBoms.map(bom => (
                    <div
                      key={bom.id}
                      className={clsx(
                        'p-4 rounded-lg border-2 transition-all',
                        loadedSavedBomId === bom.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      )}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          {editingBomId === bom.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editingBomName}
                                onChange={e => setEditingBomName(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') renameSavedBom(bom.id, editingBomName);
                                  if (e.key === 'Escape') { setEditingBomId(null); setEditingBomName(''); }
                                }}
                                className="flex-1 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                              <button
                                onClick={() => renameSavedBom(bom.id, editingBomName)}
                                className="px-2 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => { setEditingBomId(null); setEditingBomName(''); }}
                                className="px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <h4 className="font-semibold text-gray-900">{bom.name}</h4>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-600">
                                {bom.assemblyName && (
                                  <span className="px-2 py-0.5 rounded bg-gray-100">{bom.assemblyName}</span>
                                )}
                                <span>{bom.width}" × {bom.height}"</span>
                                <span className="font-medium text-green-600">{formatCurrency(bom.totalCost)}</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-1">
                                Created by {bom.createdBy} • {new Date(bom.createdAt).toLocaleDateString()}
                              </p>
                            </>
                          )}
                        </div>
                        {editingBomId !== bom.id && (
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => loadSavedBom(bom.id)}
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => { setEditingBomId(bom.id); setEditingBomName(bom.name); }}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Rename"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => deleteSavedBom(bom.id)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                      {loadedSavedBomId === bom.id && (
                        <div className="mt-2 pt-2 border-t border-blue-200">
                          <span className="text-xs font-medium text-blue-600">Currently loaded</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowSavedBomsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Special Order Modal */}
      {showSpecialOrderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">Create Special Order</h3>
              <button onClick={() => setShowSpecialOrderModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Assembly Info */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-purple-900">{selectedAssembly?.name || 'Custom BOM'}</h4>
                    {width && height && (
                      <p className="text-sm text-purple-700">{width}" × {height}"</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-purple-600">BOM Cost</div>
                    <div className="text-xl font-bold text-purple-900">{formatCurrency(activeTotalCost)}</div>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Customer Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dealer <span className="text-gray-400 font-normal">(bills to their portal + applies their discount)</span></label>
                    <select
                      value={selectedDealerId}
                      onChange={e => {
                        const id = e.target.value;
                        setSelectedDealerId(id);
                        const d = dealers.find((x: any) => x.id === id);
                        if (d) setSpecialOrderData(prev => ({ ...prev, customerName: d.company_name || prev.customerName }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">— No dealer (quote only, no discount / not payable in portal) —</option>
                      {dealers.map((d: any) => (
                        <option key={d.id} value={d.id}>{d.company_name}{d.discount_percent != null ? ` — ${d.discount_percent}% off` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer / Company Name</label>
                    <input
                      type="text"
                      value={specialOrderData.customerName}
                      onChange={e => setSpecialOrderData({ ...specialOrderData, customerName: e.target.value })}
                      placeholder="Enter customer name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={specialOrderData.customerEmail}
                      onChange={e => setSpecialOrderData({ ...specialOrderData, customerEmail: e.target.value })}
                      placeholder="customer@email.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={specialOrderData.customerPhone}
                      onChange={e => setSpecialOrderData({ ...specialOrderData, customerPhone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Shipping Address</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={specialOrderData.shippingAddress.street}
                      onChange={e => setSpecialOrderData({ ...specialOrderData, shippingAddress: { ...specialOrderData.shippingAddress, street: e.target.value } })}
                      placeholder="Street Address"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      value={specialOrderData.shippingAddress.city}
                      onChange={e => setSpecialOrderData({ ...specialOrderData, shippingAddress: { ...specialOrderData.shippingAddress, city: e.target.value } })}
                      placeholder="City"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={specialOrderData.shippingAddress.state}
                      onChange={e => setSpecialOrderData({ ...specialOrderData, shippingAddress: { ...specialOrderData.shippingAddress, state: e.target.value } })}
                      placeholder="State"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    <input
                      type="text"
                      value={specialOrderData.shippingAddress.zip}
                      onChange={e => setSpecialOrderData({ ...specialOrderData, shippingAddress: { ...specialOrderData.shippingAddress, zip: e.target.value } })}
                      placeholder="ZIP"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Freight Quotes */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Shipping Options</h4>

                {/* Accessorials */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={freightAccessorials.liftgate}
                      onChange={e => setFreightAccessorials({ ...freightAccessorials, liftgate: e.target.checked })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    Liftgate (+$75)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={freightAccessorials.residential}
                      onChange={e => setFreightAccessorials({ ...freightAccessorials, residential: e.target.checked })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    Residential (+$45)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={freightAccessorials.inside}
                      onChange={e => setFreightAccessorials({ ...freightAccessorials, inside: e.target.checked })}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    Inside Delivery (+$125)
                  </label>
                </div>

                {/* Get Quotes Button */}
                <button
                  onClick={fetchFreightQuotes}
                  disabled={loadingFreightQuotes || !specialOrderData.shippingAddress.zip}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loadingFreightQuotes ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Getting Quotes...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Get Freight Quotes
                    </>
                  )}
                </button>

                {/* Error Message */}
                {freightError && (
                  <div className="mt-2 p-2 bg-red-50 text-red-700 text-sm rounded">
                    {freightError}
                  </div>
                )}

                {/* Freight Quotes List */}
                {freightQuotes.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                    {freightQuotes.map((quote, idx) => (
                      <label
                        key={quote.quoteId || idx}
                        className={clsx(
                          'flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors',
                          selectedFreightQuote?.quoteId === quote.quoteId
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="freightQuote"
                            checked={selectedFreightQuote?.quoteId === quote.quoteId}
                            onChange={() => {
                              setSelectedFreightQuote(quote);
                              setSpecialOrderData(prev => ({ ...prev, shippingCost: quote.totalAmount }));
                            }}
                            className="text-purple-600 focus:ring-purple-500"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{quote.carrierName}</div>
                            <div className="text-sm text-gray-500">
                              {quote.serviceDescription || 'Standard'} • {quote.transitTime}
                            </div>
                          </div>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          {formatCurrency(quote.totalAmount)}
                        </div>
                      </label>
                    ))}
                  </div>
                )}

                {/* Manual Override */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shipping Cost {freightQuotes.length > 0 && <span className="text-gray-400">(or override)</span>}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={specialOrderData.shippingCost}
                      onChange={e => {
                        setSpecialOrderData({ ...specialOrderData, shippingCost: parseFloat(e.target.value) || 0 });
                        setSelectedFreightQuote(null);
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      min="0"
                      step="0.01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 pl-7"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Pricing Summary</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Markup %</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={specialOrderData.markupPercent}
                        onChange={e => setSpecialOrderData({ ...specialOrderData, markupPercent: parseFloat(e.target.value) || 0 })}
                        onWheel={(e) => e.currentTarget.blur()}
                        min="0"
                        max="1000"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 pr-8"
                      />
                      <span className="absolute right-3 top-2 text-gray-500">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sell Price</label>
                    <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg font-bold text-green-800">
                      {formatCurrency(activeTotalCost * (1 + specialOrderData.markupPercent / 100))}
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>BOM Cost:</span>
                    <span className="text-gray-500">{formatCurrency(activeTotalCost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>+ Markup ({specialOrderData.markupPercent}%):</span>
                    <span className="text-gray-500">{formatCurrency(activeTotalCost * (specialOrderData.markupPercent / 100))}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-1 mt-1">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(activeTotalCost * (1 + specialOrderData.markupPercent / 100))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Shipping{selectedFreightQuote ? ` (${selectedFreightQuote.carrierName})` : ''}:</span>
                    <span>{formatCurrency(specialOrderData.shippingCost)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t">
                    <span>Total:</span>
                    <span className="text-green-600">{formatCurrency(activeTotalCost * (1 + specialOrderData.markupPercent / 100) + specialOrderData.shippingCost)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes (not shown to customer)</label>
                <textarea
                  value={specialOrderData.internalNotes}
                  onChange={e => setSpecialOrderData({ ...specialOrderData, internalNotes: e.target.value })}
                  rows={2}
                  placeholder="Internal notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-between items-center bg-gray-50">
              <button
                onClick={() => setShowSpecialOrderModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    setCreatingSpecialOrder(true);
                    try {
                      // Use custom BOM items for custom builds, otherwise calculated BOM from bomLines
                      const bomData = selectedAssembly?.isCustom
                        ? customBomItems.map(item => ({
                            partNumber: item.partNumber,
                            description: item.description,
                            quantity: item.quantity,
                            unitCost: item.unitCost || 0,
                            total: calculateItemCost(item),
                            cutSize: item.cutSize,
                          }))
                        : bomLines.map(line => ({
                            partNumber: line.partNumber,
                            description: line.description,
                            quantity: line.quantity,
                            unitCost: line.unitCost,
                            total: line.totalCost,
                            cutSize: line.cutLength,
                          }));

                      // Use activeTotalCost to handle both custom and standard BOMs
                      const effectiveBomCost = selectedAssembly?.isCustom ? customBomTotal : totalBomCost;

                      const response = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/orders/special`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('admin_auth_token')}`,
                        },
                        body: JSON.stringify({
                          dealerId: selectedDealerId || undefined,
                          customerName: specialOrderData.customerName,
                          customerEmail: specialOrderData.customerEmail,
                          customerPhone: specialOrderData.customerPhone,
                          shippingAddress: specialOrderData.shippingAddress,
                          items: [{
                            assemblyName: selectedAssembly?.name || 'Custom Assembly',
                            description: width && height ? `${width}" × ${height}"` : '',
                            bomData,
                            bomCost: effectiveBomCost,
                            markupPercent: specialOrderData.markupPercent,
                            width: parseFloat(width) || undefined,
                            height: parseFloat(height) || undefined,
                            options,
                          }],
                          defaultMarkupPercent: specialOrderData.markupPercent,
                          specialOrderType: 'assembly',
                          internalNotes: specialOrderData.internalNotes,
                          shippingCost: specialOrderData.shippingCost,
                          shippingCarrier: selectedFreightQuote?.carrierName,
                          sendInvoiceEmail: !!specialOrderData.customerEmail,
                        }),
                      });

                      if (!response.ok) {
                        const errBody = await response.json().catch(() => ({}));
                        throw new Error(errBody.message || JSON.stringify(errBody) || 'Failed to create order');
                      }
                      const result = await response.json();

                      alert(`Special Order ${result.orderNumber} created successfully!${specialOrderData.customerEmail ? ' Invoice email sent.' : ''}`);
                      setShowSpecialOrderModal(false);
                      setSpecialOrderData({
                        customerName: '',
                        customerEmail: '',
                        customerPhone: '',
                        markupPercent: 400,
                        shippingAddress: { street: '', city: '', state: '', zip: '' },
                        shippingCost: 0,
                        internalNotes: '',
                        customerNotes: '',
                      });
                      // Reset freight state
                      setFreightQuotes([]);
                      setSelectedFreightQuote(null);
                      setFreightError('');
                      setFreightAccessorials({ liftgate: false, residential: false, inside: false });
                    } catch (err: any) {
                      alert('Error: ' + err.message);
                    } finally {
                      setCreatingSpecialOrder(false);
                    }
                  }}
                  disabled={creatingSpecialOrder || !specialOrderData.customerName}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {creatingSpecialOrder ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Order & Send Invoice
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Assembly Build Panel - Collapsible sidebar */}
      {multiBuildItems.length > 0 && (
        <div className={clsx(
          'fixed right-0 top-0 h-full bg-white shadow-2xl border-l border-gray-200 z-40 transition-all duration-300',
          showMultiBuild ? 'w-96' : 'w-12'
        )}>
          {/* Toggle button */}
          <button
            onClick={() => setShowMultiBuild(!showMultiBuild)}
            className="absolute -left-10 top-1/2 -translate-y-1/2 bg-purple-600 text-white p-2 rounded-l-lg shadow-lg hover:bg-purple-700"
            title={showMultiBuild ? 'Collapse build panel' : 'Expand build panel'}
          >
            <svg className={clsx('w-5 h-5 transition-transform', showMultiBuild ? 'rotate-0' : 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Collapsed state - just show count badge */}
          {!showMultiBuild && (
            <div className="h-full flex flex-col items-center pt-4">
              <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                {multiBuildItems.length}
              </div>
              <span className="text-xs text-gray-500 mt-2 writing-mode-vertical" style={{ writingMode: 'vertical-rl' }}>
                Build List
              </span>
            </div>
          )}

          {/* Expanded state */}
          {showMultiBuild && (
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 bg-purple-50">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Multi-Assembly Build
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {multiBuildItems.length} assemblies • {multiBuildPartCount} parts
                    </p>
                  </div>
                  <button
                    onClick={clearMultiBuild}
                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                    title="Clear all assemblies"
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Assembly list */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {multiBuildItems.map((assembly, index) => (
                  <div key={assembly.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="text-xs text-gray-500">#{index + 1}</span>
                        <h4 className="font-medium text-gray-900">{assembly.assemblyName}</h4>
                      </div>
                      <button
                        onClick={() => removeAssemblyFromBuild(assembly.id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        title="Remove from build"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div className="flex justify-between">
                        <span>Dimensions:</span>
                        <span className="font-mono">{assembly.width}" × {assembly.height}"</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Frame:</span>
                        <span className="capitalize">{assembly.frameColor}</span>
                      </div>
                      {assembly.fabricType && (
                        <div className="flex justify-between">
                          <span>Fabric:</span>
                          <span className="capitalize">{assembly.fabricType} / {assembly.fabricColor}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Parts:</span>
                        <span>{assembly.items.length} items</span>
                      </div>
                      <div className="flex justify-between font-medium pt-1 border-t border-gray-200 mt-1">
                        <span>Cost:</span>
                        <span className="text-green-700">
                          {formatCurrency(assembly.items.reduce((sum, item) => sum + (item.quantity * item.unitCost), 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Assemblies:</span>
                    <span className="font-medium">{multiBuildItems.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Parts:</span>
                    <span className="font-medium">{multiBuildPartCount}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                    <span>Total Cost:</span>
                    <span className="text-green-700">{formatCurrency(multiBuildTotal)}</span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="mt-4 space-y-2">
                  <button
                    onClick={() => {
                      // Export combined BOM as CSV
                      const headers = ['Assembly', 'Part #', 'Description', 'Cut Length', 'Qty', 'Unit Cost', 'Total'];
                      const rows: string[][] = [];

                      multiBuildItems.forEach(assembly => {
                        assembly.items.forEach(item => {
                          rows.push([
                            assembly.assemblyName,
                            item.partNumber,
                            item.description,
                            item.cutLength ? `${item.cutLength.toFixed(3)}"` : '',
                            String(item.quantity),
                            `$${item.unitCost.toFixed(2)}`,
                            `$${(item.quantity * item.unitCost).toFixed(2)}`,
                          ]);
                        });
                      });

                      // Add totals row
                      rows.push(['', '', '', '', '', 'TOTAL:', `$${multiBuildTotal.toFixed(2)}`]);

                      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `multi-assembly-bom-${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export Combined BOM
                  </button>
                  <button
                    onClick={() => {
                      // Print combined BOM
                      const printWindow = window.open('', '_blank');
                      if (!printWindow) return;

                      const content = `
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>Multi-Assembly BOM - ${new Date().toLocaleDateString()}</title>
                          <style>
                            body { font-family: Arial, sans-serif; padding: 20px; }
                            h1 { font-size: 18px; margin-bottom: 20px; }
                            h2 { font-size: 14px; margin: 15px 0 10px; background: #f5f5f5; padding: 8px; }
                            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
                            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
                            th { background: #f9f9f9; }
                            .text-right { text-align: right; }
                            .font-bold { font-weight: bold; }
                            .total-row { background: #f0f9f0; font-weight: bold; }
                            .summary { margin-top: 30px; padding: 15px; background: #f5f5f5; }
                          </style>
                        </head>
                        <body>
                          <h1>Multi-Assembly BOM</h1>
                          <p>Generated: ${new Date().toLocaleString()}</p>

                          ${multiBuildItems.map((assembly, i) => `
                            <h2>#${i + 1} ${assembly.assemblyName} - ${assembly.width}" × ${assembly.height}" (${assembly.frameColor})</h2>
                            <table>
                              <thead>
                                <tr>
                                  <th>Part #</th>
                                  <th>Description</th>
                                  <th class="text-right">Cut Length</th>
                                  <th class="text-right">Qty</th>
                                  <th class="text-right">Unit Cost</th>
                                  <th class="text-right">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                ${assembly.items.map(item => `
                                  <tr>
                                    <td>${item.partNumber}</td>
                                    <td>${item.description}</td>
                                    <td class="text-right">${item.cutLength ? item.cutLength.toFixed(3) + '"' : '-'}</td>
                                    <td class="text-right">${item.quantity}</td>
                                    <td class="text-right">$${item.unitCost.toFixed(2)}</td>
                                    <td class="text-right">$${(item.quantity * item.unitCost).toFixed(2)}</td>
                                  </tr>
                                `).join('')}
                                <tr class="total-row">
                                  <td colspan="5" class="text-right">Assembly Total:</td>
                                  <td class="text-right">$${assembly.items.reduce((sum, item) => sum + (item.quantity * item.unitCost), 0).toFixed(2)}</td>
                                </tr>
                              </tbody>
                            </table>
                          `).join('')}

                          <div class="summary">
                            <p><strong>Total Assemblies:</strong> ${multiBuildItems.length}</p>
                            <p><strong>Total Parts:</strong> ${multiBuildPartCount}</p>
                            <p><strong>Combined Total:</strong> $${multiBuildTotal.toFixed(2)}</p>
                          </div>
                        </body>
                        </html>
                      `;

                      printWindow.document.write(content);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                    className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Print Combined BOM
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
